import { ChangeDetectorRef, Input, OnDestroy } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import * as THREE from 'three';
import { FikoThreeJsComponent } from '../../../fiko-three-js.component';
import { FikoRaycastCPUYardimci } from '../../FikoRaycastCPUYardimci';
import { FikoVektor3D } from '../../FikoVektor3d';
import { agirlikMerkezi, yuzeyDuzlemAl, hesaplaAlan, miYuzeyDuzlemsel, alCizgiYon, alCizgiUzunluk, miCizgiYon, miCember, sokToleransa, yuzeyTipiTespit, cizgiEgrilikiTespit } from '../../FikoYT-Fonksiyonlar';
import { FikoYTService } from '@/src/service/fikoyt.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FikoRaycastConfigService } from '../../../services/fiko-raycast-config.service';
import { OlcumTuru, FikoOlcumCizgisi } from '../../FikoOlcumCizgisi';
import { FikoEksen } from '../../FikoEksen';
import { geometryAnalysisCache } from '../GeometryAnalysisCache';
import { FikoOlcumBase, SecimTuru } from '../fiko-olcum-base';
import { FikoKaliciOlcumler } from '../../FikoKaliciOlcumler';
import { FikoKaynakca } from '../../FikoThreeCam/FikoKaynakca';
import { DimensionIntegrationService } from '../dimension/DimensionIntegration.service';
import { flatness, cylindricity } from '../gdt/FikoGDTVerify';
import { FikoGDTAnnotator, GDT_SEMBOL } from '../gdt/FikoGDTAnnotator';

declare var ClipperLib: any;

@Component({
  selector: 'fikoyt-kumpas',
  templateUrl: './kumpas.component.html',
  styleUrls: ['./kumpas.component.scss'],
  imports: [CommonModule, FormsModule]
})
export class KumpasComponent extends FikoOlcumBase implements OnInit, OnDestroy {
  // ========== SESSION & KALICI ÖLÇÜM SİSTEMİ ==========
  /** Her dialog açılışında benzersiz session ID üretilir */
  sessionId: string = '';
  /** Kalıcı ölçümler grubu referansı (FikoKaynakca altında) */
  private kaliciOlcumlerGrubu: FikoKaliciOlcumler | null = null;

  // 🚀 PRE-ALLOCATED BUFFERS (zero-allocation loops)
  private static readonly _tempVec1 = new THREE.Vector3();
  private static readonly _tempVec2 = new THREE.Vector3();
  private static readonly _tempVec3 = new THREE.Vector3();
  private static readonly _tempAxis = new THREE.Vector3();
  private static readonly _tempCentroid = new THREE.Vector3();
  private static readonly _tempCross = new THREE.Vector3();
  private static readonly _tempRel = new THREE.Vector3();
  private static readonly _tempUVec = new THREE.Vector3();
  private static readonly _tempVVec = new THREE.Vector3();
  private static readonly _tempCapYonu = new THREE.Vector3();
  // 🚀 Final hesaplamalar için bufferlar
  private static readonly _tempGercekMerkez = new THREE.Vector3();
  private static readonly _tempTikRel = new THREE.Vector3();
  private static readonly _tempCapMerkez = new THREE.Vector3();

  // ========== KUMPAS-SPECIFIC PROPERTIES ==========
  sonucTipi: "Nokta" | "Çizgi" | "Yüzey" | "Grup" | null = null;

  // Kumpas'a özel: alan basımı
  alanBas: number = 10;

  // Kumpas sonuç objesi (kumpas'a özel yapı)
  sonuc: {
    nokta?: {
      x?: number | string, y?: number | string, z?: number | string,
      vektor?: FikoVektor3D,
    },
    yuzey?: {
      alan?: number | string,
      agirlikMerkezi?: FikoVektor3D,
      duzlem?: THREE.Plane,
      miDuzlemsel?: boolean,
      tipi?: string,
      tipiBilgisi?: any,
      gdt?: { etiket: string, sapma: number, tip?: string, anchor?: THREE.Vector3 },
    },
    cizgi?: {
      uzunluk?: number,
      miDuzlemsel?: boolean,
      yon?: THREE.Vector3,
      cember?: any,
      egriliBilgi?: any,
    },
  } = null;

  // 🎯 NOKTA KOY: Kumpas'a özel hover state
  private hoverCrosshairGroup: THREE.Group | null = null;
  private hoverSnapType: 'vertex' | 'edge' | 'surface' | 'none' = 'none';

  // 🎯 YENİ DIMENSION SİSTEMİ
  private dimensionService: DimensionIntegrationService | null = null;
  useNewDimensionSystem: boolean = true;  // true = yeni sistem, false = eski FikoOlcumCizgisi
  /** Klasik uzatma çizgisi: true = eski paralel witness (sürüklemede) */
  klasikUzatma: boolean = false;

  // 🎯 GD&T FCF sembol yöneticisi (3D sahnede tolerans sembolleri)
  private gdtAnnotator: FikoGDTAnnotator = new FikoGDTAnnotator(null);

  // 🎯 AKILLI ÇAP: dairesel kenar hover'da çap önizleme. Sprite/canvas REUSE + kenar-değişince
  // cache (_sonCemberEdgeId) → her mousemove'da hesap YOK, fit YOK (BREP metadata'dan radius) → donmasız.
  private cemberPreviewSprite: THREE.Sprite | null = null;
  private cemberPreviewCanvas: HTMLCanvasElement | null = null;
  private cemberPreviewTexture: THREE.CanvasTexture | null = null;
  private cemberPreviewSonCap: number = -1;
  private _sonCemberEdgeId: number | undefined = undefined;

  // 🎯 ALAN 3D: yüzey alanı sahne etiketi (sprite reuse)
  private alanEtiketSprite: THREE.Sprite | null = null;
  private alanEtiketCanvas: HTMLCanvasElement | null = null;
  private alanEtiketTexture: THREE.CanvasTexture | null = null;
  private alanEtiketSonAlan: number = -1;

  constructor(
    FikoYT: FikoYTService,
    CD: ChangeDetectorRef,
    raycastConfigService: FikoRaycastConfigService
  ) {
    super(FikoYT, CD, raycastConfigService);
  }

  /**
   * Kaydedilen ayarları yükle - base class'ın metodunu override
   */
  override ngOnInit() {
    super.loadAyarlar();
  }

  async baslat(anaGosterim: FikoThreeJsComponent) {
    this.bitir();
    this.anaGosterim = anaGosterim;

    // 🎯 SESSION ID: Her dialog açılışında benzersiz ID üret
    this.sessionId = this.generateSessionId();

    // 🚀 Base class metodlarını kullan
    await this.initGPUOlcumMotoru();
    this.initOlcumlerGrubu();

    // 🎯 KALICI ÖLÇÜMLER: FikoKaynakca altındaki kalıcı ölçümler grubunu bul
    const sahneFiko = this.anaGosterim.sahneler[0] as any;
    this.kaliciOlcumlerGrubuBul(sahneFiko);

    // FikoSahne'deki tek instance'ı kullan
    this.fikoSecim1 = this.anaGosterim.sahneler[0].secimler;
    // 🎯 ARAÇ STATE YEDEK: araç kapanınca bitir() geri yükler (araçlar arası karışma fix)
    this.fikoSecim1.durumYedekle?.();
    this._secimDurumYedekli = true;
    this.setupFikoSecililer(this.fikoSecim1);

    // 🎯 HOVER HIGHLIGHT: Ayarlardan yükle
    this.hoverAyarlariYukle();

    // Material ayarları
    this.fikoSecim1.highlightSaydamlik = this.highlightSaydamlik;

    // 🎯 YENİ DIMENSION SİSTEMİ: Servisi başlat
    if (this.useNewDimensionSystem) {
      this.initDimensionService();
    }

    this.fikoSecimOlaylarTanit();
    //@ts-ignore
    document.body.style.cursor = "url('/cursor/kumpas-bos.webp'), auto";

    // 🎯 BREP CURSOR: Varsayılan cursor'u kaydet
    this.varsayilanCursor = "url('/cursor/kumpas-bos.webp'), auto";
  }

  // 🎯 BREP CURSOR: Surface type'a göre cursor değiştirme
  private varsayilanCursor: string = "url('/cursor/kumpas-bos.webp'), auto";
  private sonCursor: string = '';

  // 🎯 ARAÇ STATE YEDEK: baslat'ta FikoSecililer durumu yedeklendi mi (bitir'de geri yüklenir)
  private _secimDurumYedekli: boolean = false;

  /**
   * 🎯 BREP CURSOR: Surface type'a göre cursor'u güncelle
   * PLANE → kumpas-yuzey-duzlem
   * CYLINDER/SPHERE/CONE/TORUS → kumpas-yuzey-dairesel
   * BSPLINE/BEZIER/OTHER → kumpas-yuzey-dalgali
   * LINE → kumpa-cizgi-duz
   * CIRCLE → kumpa-cizgi-dairesel
   */
  private cursorGuncelle(kesisim: any): void {
    if (!this.fikoSecim1?.hoverHighlightEnabled) {
      // Hover kapalıysa varsayılan cursor
      if (this.sonCursor !== this.varsayilanCursor) {
        document.body.style.cursor = this.varsayilanCursor;
        this.sonCursor = this.varsayilanCursor;
      }
      return;
    }

    if (!kesisim || !kesisim.object) {
      // Kesişim yoksa varsayılan cursor
      if (this.sonCursor !== this.varsayilanCursor) {
        document.body.style.cursor = this.varsayilanCursor;
        this.sonCursor = this.varsayilanCursor;
      }
      return;
    }

    let yeniCursor = this.varsayilanCursor;
    const altObje = (kesisim as any).altObje;
    const obj = kesisim.object;

    // Nesne tipine göre cursor belirle
    // ÖNCELİK: Çizgi > Nokta > Yüzey (altObje öncelikli)
    if (altObje?.type === 'LineSegments' || altObje?.type === 'LineSegments2' ||
        altObje?.type === 'Line2' || altObje?.type === 'Line') {
      // ÇİZGİ - altObje çizgi ise
      yeniCursor = this.cizgiIcinCursorBelirle(kesisim);
    } else if (obj?.type === 'LineSegments' || obj?.type === 'LineSegments2' ||
               obj?.type === 'Line2' || obj?.type === 'Line') {
      // ÇİZGİ - obj çizgi ise
      yeniCursor = this.cizgiIcinCursorBelirle(kesisim);
    } else if (altObje?.type === 'Points' || obj?.type === 'Points') {
      // NOKTA
      yeniCursor = "url('/cursor/kumpas-nokta.png'), auto";
    } else if (altObje?.type === 'Mesh' || obj?.type === 'Mesh' || obj?.type === 'FikoCizimYalin') {
      // YÜZEY - Surface type'a göre cursor
      yeniCursor = this.yuzeyIcinCursorBelirle(kesisim);
    }

    // Cursor değiştiyse güncelle
    if (this.sonCursor !== yeniCursor) {
      document.body.style.cursor = yeniCursor;
      this.sonCursor = yeniCursor;
    }
  }

  /**
   * 🎯 YÜZEY CURSOR: Surface type'a göre cursor belirle
   * FikoSecililer'den gelen hover highlight'ın grup bilgisini kullanır
   */
  private yuzeyIcinCursorBelirle(kesisim: any): string {
    // 🎯 FIX: FikoSecililer'in sonSecilenGrupId'sini kullan (hover için doğru grup)
    // Bu değer createCizimYalinHighlight'ta hesaplanıyor
    const grpId = this.fikoSecim1?.sonSecilenGrupIdUzerinde ?? -1;

    // 1. occt_faceGroups'tan surface type al
    const cizim = kesisim.object;
    const faceGroups = cizim?.occt_faceGroups || (cizim?.parent as any)?.occt_faceGroups;

    if (faceGroups && Array.isArray(faceGroups) && grpId >= 0 && grpId < faceGroups.length) {
      const faceInfo = faceGroups[grpId];
      const surfaceType = faceInfo?.surfaceType || faceInfo?.underlyingType;

      if (surfaceType) {
        return this.surfaceTypeToCursor(surfaceType);
      }
    }

    // 2. Fallback: kesisim.faceIndex'ten hesapla
    if (faceGroups && Array.isArray(faceGroups)) {
      const mesh = (kesisim as any).altObje || kesisim.object;
      const geom = mesh?.geometry || cizim?.meshGeom;

      if (geom?.attributes?.grup && kesisim.faceIndex !== undefined) {
        const grupAttr = geom.attributes.grup;
        const vertexIdx = kesisim.faceIndex * 3;
        if (vertexIdx < grupAttr.count) {
          const fallbackGrpId = grupAttr.getZ(vertexIdx);

          if (fallbackGrpId >= 0 && fallbackGrpId < faceGroups.length) {
            const faceInfo = faceGroups[fallbackGrpId];
            const surfaceType = faceInfo?.surfaceType || faceInfo?.underlyingType;

            if (surfaceType) {
              return this.surfaceTypeToCursor(surfaceType);
            }
          }
        }
      }

      // 3. Geometry attribute'undan surface type al
      if (geom?.attributes?.surfaceType && kesisim.faceIndex !== undefined) {
        const surfaceTypeAttr = geom.attributes.surfaceType;
        const vertexIdx = kesisim.faceIndex * 3;
        if (vertexIdx < surfaceTypeAttr.count) {
          const surfaceTypeCode = surfaceTypeAttr.getX(vertexIdx);
          return this.surfaceTypeCodeToCursor(surfaceTypeCode);
        }
      }
    }

    // Varsayılan yüzey cursor
    return "url('/cursor/kumpas-yuzey-duzlem.png'), auto";
  }

  /**
   * 🎯 ÇİZGİ CURSOR: Çizgi tipine göre cursor belirle (BREP curveType öncelikli)
   */
  private cizgiIcinCursorBelirle(kesisim: any): string {
    // 1. BREP curveType bilgisini dene (en doğru yöntem)
    const edgeGrupId = this.fikoSecim1?.sonSecilenEdgeGrupIdUzerinde ?? -1;

    // FikoCizimYalin VEYA occt metadata taşıyan modelleme gövdesini bul
    let cizim = kesisim.object;
    while (cizim && cizim.type !== 'FikoCizimYalin' && !(cizim as any).occt_edgeGroups) {
      cizim = cizim.parent;
    }

    // occt_edgeGroups metadata'sını kontrol et
    const edgeGroups = (cizim as any)?.occt_edgeGroups;

    if (edgeGrupId >= 0 && edgeGroups && edgeGrupId < edgeGroups.length) {
      const curveType = edgeGroups[edgeGrupId]?.curveType;
      if (curveType) {
        return this.curveTypeToCursor(curveType);
      }
    }

    // 2. Fallback: Geometri analizi ile eğrilik tespiti
    const secilen = (kesisim as any).altObje || kesisim.object;
    if (!secilen?.geometry) {
      return "url('/cursor/kumpa-cizgi-duz.png'), auto";
    }

    // Highlight geometrisini kullan (daha doğru)
    const geom = this.fikoSecim1?.sonSecilenHighlightGeometri || secilen.geometry;
    const posAttr = geom?.attributes?.position;

    if (posAttr && posAttr.count >= 3) {
      // Basit eğrilik tespiti: vertex'ler arasındaki açı değişimi
      const v1 = new THREE.Vector3(posAttr.getX(0), posAttr.getY(0), posAttr.getZ(0));
      const v2 = new THREE.Vector3(posAttr.getX(Math.floor(posAttr.count / 2)), posAttr.getY(Math.floor(posAttr.count / 2)), posAttr.getZ(Math.floor(posAttr.count / 2)));
      const v3 = new THREE.Vector3(posAttr.getX(posAttr.count - 1), posAttr.getY(posAttr.count - 1), posAttr.getZ(posAttr.count - 1));

      const dir1 = v2.clone().sub(v1).normalize();
      const dir2 = v3.clone().sub(v2).normalize();
      const dot = dir1.dot(dir2);

      if (dot < 0.99) {
        // Eğri çizgi - dairesel veya dalgalı
        // miCember kontrolü ile tam çember mi kontrol et
        const miCem = miCember(geom);
        if (miCem && miCem.yaricap > 0.1) {
          return "url('/cursor/kumpa-cizgi-dairesel.png'), auto";
        }
        return "url('/cursor/kumpa-cizgi-dalgali.png'), auto";
      }
    }

    // Düz çizgi
    return "url('/cursor/kumpa-cizgi-duz.png'), auto";
  }

  /**
   * Curve type string'den cursor path'e çevir
   */
  private curveTypeToCursor(curveType: string): string {
    switch (curveType?.toUpperCase()) {
      case 'LINE':
        return "url('/cursor/kumpa-cizgi-duz.png'), auto";
      case 'CIRCLE':
      case 'ELLIPSE':
        return "url('/cursor/kumpa-cizgi-dairesel.png'), auto";
      case 'BSPLINE':
      case 'BEZIER':
      case 'HYPERBOLA':
      case 'PARABOLA':
      case 'OFFSET':
      case 'OTHER':
      default:
        return "url('/cursor/kumpa-cizgi-dalgali.png'), auto";
    }
  }

  /**
   * Surface type string'den cursor path'e çevir
   */
  private surfaceTypeToCursor(surfaceType: string): string {
    switch (surfaceType?.toUpperCase()) {
      case 'PLANE':
        return "url('/cursor/kumpas-yuzey-duzlem.png'), auto";
      case 'CYLINDER':
      case 'SPHERE':
      case 'CONE':
      case 'TORUS':
        return "url('/cursor/kumpas-yuzey-dairesel.png'), auto";
      case 'BSPLINE':
      case 'BEZIER':
      case 'REVOLUTION':
      case 'EXTRUSION':
      case 'OFFSET':
      case 'OTHER':
      default:
        return "url('/cursor/kumpas-yuzey-dalgali.png'), auto";
    }
  }

  /**
   * Surface type code'dan cursor path'e çevir
   * 0=UNKNOWN, 1=PLANE, 2=CYLINDER, 3=SPHERE, 4=CONE, 5=TORUS, 6=BEZIER, 7=BSPLINE, 8=REVOLUTION, 9=EXTRUSION, 10=OFFSET, 11=OTHER
   */
  private surfaceTypeCodeToCursor(code: number): string {
    switch (code) {
      case 1: // PLANE
        return "url('/cursor/kumpas-yuzey-duzlem.png'), auto";
      case 2: // CYLINDER
      case 3: // SPHERE
      case 4: // CONE
      case 5: // TORUS
        return "url('/cursor/kumpas-yuzey-dairesel.png'), auto";
      case 6: // BEZIER
      case 7: // BSPLINE
      case 8: // REVOLUTION
      case 9: // EXTRUSION
      case 10: // OFFSET
      case 11: // OTHER
      case 0: // UNKNOWN
      default:
        return "url('/cursor/kumpas-yuzey-dalgali.png'), auto";
    }
  }

  /**
   * 🎯 YENİ DIMENSION SİSTEMİ: DimensionIntegrationService'i başlat
   */
  private initDimensionService(): void {
    // console.log('[Kumpas] initDimensionService called, anaGosterim:', !!this.anaGosterim, 'sahneler:', !!this.anaGosterim?.sahneler[0]);
    if (!this.anaGosterim?.sahneler[0]) return;

    const fikoSahne = this.anaGosterim.sahneler[0];
    this.dimensionService = new DimensionIntegrationService();
    this.dimensionService.initialize(fikoSahne, this.fikoOlcumlerGrubu);
    this.gdtAnnotator.setGrup(this.fikoOlcumlerGrubu as any); // GD&T FCF sembolleri bu gruba eklenir
    // console.log('[Kumpas] dimensionService initialized:', !!this.dimensionService);

    // Ayarları uygula - 🎯 v3.2.6: Cetvel ile aynı şekilde dinamik birim
    this.dimensionService.updateSettings({
      birim: 'mm', // Kumpas her zaman 'mm' kullanır (tek seçimli araç)
      hassasiyet: this.koordinatBas,
      siyahStil: true
    });

    // 🎯 v3.2.10: SÜRÜKLEME DESTEĞİNİ AKTİF ET (çap/yarıçap ölçümleri için kritik!)
    const canvas = this.anaGosterim?.sunucu?.domElement;
    if (canvas) {
      const manager = this.dimensionService.getManager();
      if (manager) {
        manager.enableDragSupport(canvas);
        // console.log('[Kumpas] v3.2.10 Sürükleme desteği aktif edildi');
      }
    }
  }

  fikoSecimOlaylarTanit() {
    // 🎯 HOVER EVENT: Tüm modlarda hover highlight + Nokta Koy modunda mouse ile hareket eden nokta göster
    this.subsList.push(
      this.fikoSecim1.uzerinde.subscribe((kesisim) => {
        // 🎯 NOKTA KOY MODU: Yüzey highlight'ları yapma, sadece hover noktası göster
        const noktaKoyModu = Array.isArray(this.secim1Filitre) && this.secim1Filitre.indexOf("Nokta Koy") > -1;

        if (noktaKoyModu) {
          this.cemberPreviewGizle(); // Nokta Koy modunda çap önizleme gösterme
          // Nokta Koy modunda yüzey highlight'larını temizle
          if (this.fikoSecim1.grupSecimlerUzerinde) {
            this.fikoSecim1.grupSecimlerUzerinde.clear();
          }

          if (!kesisim || !kesisim.point) {
            this.hoverNoktaTemizle();
            this.anaGosterim.ciz();
            return;
          }

          // ⚡ GEOMETRIC SNAP: Snap uygula (vertex/endpoint/midpoint/center/edge)
          const snapResult = this.applySnap(kesisim.point, kesisim);
          const snapKonum = snapResult.snapped ? snapResult.position : kesisim.point;

          // Hover noktasını göster/güncelle (mouse ile hareket eder) - CATIA-style
          this.hoverNoktaGoster(snapKonum, kesisim);
          return;
        }

        // 🎯 GENEL HOVER HIGHLIGHT: Diğer modlar için çalışır
        if (kesisim && this.fikoSecim1.grupSecimlerUzerinde) {
          this.anaGosterim.ciz();
        } else if (this.fikoSecim1.grupSecimlerUzerinde) {
          this.fikoSecim1.grupSecimlerUzerinde.clear();
          this.anaGosterim.ciz();
        }

        // 🎯 BREP CURSOR: Surface type'a göre cursor güncelle
        this.cursorGuncelle(kesisim);

        // 🎯 AKILLI ÇAP: dairesel kenar hover'da çapı önizle (donmasız: kenar değişince + metadata)
        this.akilliCapOnizle(kesisim);

        // Nokta Koy modunda değilsek hover noktasını temizle
        this.hoverNoktaTemizle();
      })
    );

    this.subsList.push(
      this.fikoSecim1.tiklandi.subscribe((kesisim) => {
        // 🎯 NOKTA KOY MODU: Seçim highlight'larını yapma, sadece noktayı koy
        const noktaKoyModu = Array.isArray(this.secim1Filitre) && this.secim1Filitre.indexOf("Nokta Koy") > -1;

        if (noktaKoyModu) {
          // Nokta Koy modunda tüm highlight'ları temizle
          if (this.fikoSecim1.grupSecimler) {
            this.fikoSecim1.grupSecimler.clear();
          }
          if (this.fikoSecim1.grupSecimlerUzerinde) {
            this.fikoSecim1.grupSecimlerUzerinde.clear();
          }
        }

        // 🎯 YENİ ÖLÇÜM: Kalıcı noktaları ve gösterimi temizle
        // NOT: grupSecimler.clear() YAPMA! pointerUp'ta kesisimCozumle() highlight ekledikten SONRA
        // bu callback çağrılıyor, clear() yaparsak az eklenen highlight silinir!
        // 🎯 GÜNCELLEME: Nokta Koy modunda yukarıda temizleniyor
        // 🎯 v2.0.0 FIX: Sürükleme sırasında temizleme yapma!
        // DimensionIntegrationService'den sürükleme durumunu kontrol et
        if (this.dimensionService?.isDragging()) {
          // console.log('[Kumpas] tiklandi: Sürükleme aktif (DimensionService), temizleme atlanıyor');
          return; // Sürükleme sırasında hiçbir şey yapma
        }
        // 🎯 v2.1.0: FikoOlcumCizgisi sürüklemesini de kontrol et
        if (this.fikoOlcumCizgisi?.isDragging) {
          // console.log('[Kumpas] tiklandi: Sürükleme aktif (FikoOlcumCizgisi), temizleme atlanıyor');
          return; // Sürükleme sırasında hiçbir şey yapma
        }
        this.kaliciNoktalariTemizle();
        this.gosterimYukleTemizle();
        this.alanEtiketGizle(); // 🎯 ALAN 3D: önceki alan etiketini temizle (yüzey ölçülünce tekrar gösterilir)

        // ✅ FIX: Filtre kontrolü - eğer seçilen nesne türü filtrede yoksa işleme
        if (!kesisim || !kesisim.object) {
          this.sonucTipi = null;
          this.sonuc = null;
          this.CD.detectChanges();
          return;
        }

        // ✅ FIX: altObje'yi de kontrol et (FikoCizimYalin içindeki objeler için)
        // Gerçek tip altObje varsa altObje'nin tipi, yoksa object'in tipi
        const gercekTip = (kesisim as any).altObje?.type || kesisim.object?.type;

        // Filtre kontrol et - eğer "Her Şey" değilse ve seçilen tip filtrede yoksa
        if (this.secim1Filitre !== "*" &&
            this.secim1Filitre.indexOf("Nokta Koy") === -1 &&
            this.secim1Filitre.indexOf(gercekTip) === -1) {
          this.sonucTipi = null;
          this.sonuc = null;
          this.CD.detectChanges();
          return;
        }

        /** NOT:  Eğer Filitre Nokta Koy İse */
        /** NOT:  Seçilen Nokta İse */
        if (this.secim1Filitre.indexOf("Nokta Koy") > -1) {
          let secilen: THREE.Points = kesisim?.object as THREE.Points;

          if (!secilen) {
            this.sonucTipi = null;
            this.sonuc = null;
          } else {
            // ⚡ GEOMETRIC SNAP: Tıklamada da snap uygula
            const clickSnap = this.applySnap(kesisim.point, kesisim);
            let koor = clickSnap.snapped ? clickSnap.position.clone() : kesisim.point;

            /** NOT: Eksene Göre */
            let koor1: FikoVektor3D = koor.clone();
            koor1.sub((this.kumpasEksen || new FikoEksen()).merkez);
            koor1.applyQuaternion((this.kumpasEksen || new FikoEksen()).quaternion.clone().invert());

            this.sonucTipi = 'Nokta';
            // 🎯 v3.2.9: Gerçek değerleri sakla (yuvarlanmamış)
            this.sonuc = {
              nokta: {
                vektor: koor1,
                x: koor1.x,  // 🎯 Gerçek sayı
                y: koor1.y,  // 🎯 Gerçek sayı
                z: koor1.z   // 🎯 Gerçek sayı
              }
            }

            // ✅ Nokta ölçümü gösterimi - Koordinatlar (🎯 sadece gösterimde yuvarla)
            const noktaWorld = koor.clone();
            const label = `X: ${koor1.x.toFixed(this.koordinatBas)}\nY: ${koor1.y.toFixed(this.koordinatBas)}\nZ: ${koor1.z.toFixed(this.koordinatBas)}`;
            // 🎯 v3.3.0: Koordinat için doğrudan eski sistemi kullan (yeni sistem iki ölçüm oluşturuyordu)
            const oncekiDurum = this.useNewDimensionSystem;
            this.useNewDimensionSystem = false;
            this.gosterimKayniyaEkle(noktaWorld, noktaWorld, label, false, 0, undefined, 'koordinat');
            this.useNewDimensionSystem = oncekiDurum;

            // 🎯 NOKTA KOY: Tıklandığında kalıcı nokta ekle
            this.kaliciNoktaEkle(noktaWorld, label);

            this.CD.detectChanges();
          }

        } else
          /** NOT:  Seçilen Nokta İse */
          if ((kesisim as any)?.altObje?.type == 'Points' || kesisim?.object?.type == 'Points') {
            // FikoSecimler1'de seçilen obje kesisim'in kendisi
            // Eğer FikoCizimYali ise altObje kullanılır, değilse object kullanılır
            let secilen: THREE.Points = ((kesisim as any)?.altObje || kesisim?.object) as THREE.Points;

            if (kesisim.object?.type === "FikoCizimYalin" && (kesisim as any).altObje) {
              // FikoCizimYalin içindeki nokta
              secilen = (kesisim as any).altObje as THREE.Points;
            }

            if (!secilen) {
              this.sonucTipi = null;
              this.sonuc = null;
            } else {
              let koor = new FikoVektor3D(secilen.geometry.attributes['position'].getX(kesisim.index), secilen.geometry.attributes['position'].getY(kesisim.index), secilen.geometry.attributes['position'].getZ(kesisim.index));

              // ✅ FIX: Nokta koordinatını parent'ın transform'u ile uygula
              if (secilen.parent && (secilen.parent as any).matrixWorld) {
                koor.applyMatrix4((secilen.parent as any).matrixWorld);
              } else if (kesisim.object?.type === "FikoCizimYalin") {
                koor.applyMatrix4(((kesisim.object) as any).matrixWorld);
              }

              /** NOT: Eksene Göre */
              let koor1: FikoVektor3D = koor.clone();
              koor1.sub((this.kumpasEksen || new FikoEksen()).merkez);
              koor1.applyQuaternion((this.kumpasEksen || new FikoEksen()).quaternion.clone().invert());

              this.sonucTipi = 'Nokta';
              // 🎯 v3.2.9: Gerçek değerleri sakla (yuvarlanmamış)
              this.sonuc = {
                nokta: {
                  vektor: koor1,
                  x: koor1.x,  // 🎯 Gerçek sayı
                  y: koor1.y,  // 🎯 Gerçek sayı
                  z: koor1.z   // 🎯 Gerçek sayı
                }
              }

              // ✅ Nokta ölçümü gösterimi - Koordinatlar (🎯 sadece gösterimde yuvarla)
              const noktaWorld = koor.clone();
              const label = `X: ${koor1.x.toFixed(this.koordinatBas)}\nY: ${koor1.y.toFixed(this.koordinatBas)}\nZ: ${koor1.z.toFixed(this.koordinatBas)}`;
              // 🎯 v3.3.0: Koordinat için doğrudan eski sistemi kullan (yeni sistem iki ölçüm oluşturuyordu)
              const oncekiDurum2 = this.useNewDimensionSystem;
              this.useNewDimensionSystem = false;
              this.gosterimKayniyaEkle(noktaWorld, noktaWorld, label, false, 0, undefined, 'koordinat');
              this.useNewDimensionSystem = oncekiDurum2;
            }
            this.CD.detectChanges();

          } else
            /** NOT:  Seçilen Yüzey İse */

            if ((kesisim as any)?.altObje?.type == 'Mesh' || kesisim?.object?.type == 'Mesh') {
              // FikoSecimler1'de seçilen obje kesisim'in kendisi
              // Eğer FikoCizimYali ise altObje kullanılır, değilse object kullanılır
              let secilen: THREE.Mesh = ((kesisim as any)?.altObje || kesisim?.object) as THREE.Mesh;

              if (!secilen) {
                this.sonucTipi = null;
                this.sonuc = null;
              } else {
                this.sonucTipi = 'Yüzey';

                // ✅ MAJOR FIX: Yüzey ölçümlerini çizgi gibi highlight geometry'den al
                // 🎯 ANLIK FALLBACK: highlight cache boşsa tıklanan face grubunu doğrudan üret
                // (cache'e bağımlılık yüzey ölçümlerinin boş/0 çıkmasına yol açıyordu)
                const yuzeyGeoKaynak: THREE.BufferGeometry | null =
                  this.fikoSecim1.sonSecilenHighlightGeometriYuzey?.clone()
                  || (typeof this.fikoSecim1.seciliYuzeyGeometrisiAl === 'function'
                      ? this.fikoSecim1.seciliYuzeyGeometrisiAl(kesisim) : null);
                if (yuzeyGeoKaynak) {
                  let yuzeyGeometri = yuzeyGeoKaynak;

                  // Transform uygula
                  if (kesisim.object?.type === "FikoCizimYalin") {
                    const cizim = kesisim.object as any;
                    yuzeyGeometri.applyMatrix4(cizim.matrixWorld);
                  } else {
                    const secilen = ((kesisim as any)?.altObje || kesisim?.object) as THREE.Mesh;
                    if (secilen && secilen.parent && (secilen.parent as any).matrixWorld) {
                      yuzeyGeometri.applyMatrix4((secilen.parent as any).matrixWorld);
                    } else if (secilen && secilen.matrixWorld) {
                      yuzeyGeometri.applyMatrix4(secilen.matrixWorld);
                    }
                  }

                  /** NOT: Eksene Göre */
                  let agirlikMerkeziKoor: FikoVektor3D = agirlikMerkezi(yuzeyGeometri);
                  agirlikMerkeziKoor.sub((this.kumpasEksen || new FikoEksen).merkez);
                  agirlikMerkeziKoor.applyQuaternion((this.kumpasEksen || new FikoEksen()).quaternion.clone().invert());

                  let alan = hesaplaAlan(yuzeyGeometri);
                  let duzlemKoor = yuzeyDuzlemAl(yuzeyGeometri);
                  duzlemKoor.applyMatrix4((this.kumpasEksen || new FikoEksen()).matrixWorld.clone().invert(), new THREE.Matrix3().getNormalMatrix((this.kumpasEksen || new FikoEksen()).matrixWorld.clone()));
                  duzlemKoor.constant = yuzeyDuzlemAl(yuzeyGeometri).distanceToPoint((this.kumpasEksen || new FikoEksen).position.clone());

                  // ✅ FIX: Yüzey tipi tespiti - Silindir, Küre, Düzlem vb.
                  // 🎯 v3.2.11: Eşikler düşürüldü - silindirik yüzeyler artık doğru algılanacak
                  const yuzeyTipi = yuzeyTipiTespit(yuzeyGeometri);
                  // console.log('[Kumpas] v3.2.11 Yüzey tipi tespiti:', yuzeyTipi.tip, 'İçerik:', yuzeyTipi.icerik);

                  // 🎯 GD&T tek-yüzey form doğrulama (silindir→silindiriklik, düzlem→düzlemsellik)
                  // → 3D sahnede FCF (feature control frame) sembolü gösterir.
                  let gdtTekYuzey: any = undefined;
                  try {
                    let g: any = null;
                    let gdtTip: 'silindiriklik' | 'duzlemsellik' | null = null;
                    if (yuzeyTipi.tip === 'silindir' || (yuzeyTipi.icerik as any)?.tipi === 'silindir') {
                      g = cylindricity(yuzeyGeometri); gdtTip = 'silindiriklik';
                    } else if (yuzeyTipi.tip === 'duzlem') {
                      g = flatness(yuzeyGeometri); gdtTip = 'duzlemsellik';
                    }
                    if (g) {
                      // World-space anchor (FCF leader) = yüzey vertex ortalaması
                      const pAttr = yuzeyGeometri.attributes.position;
                      const anchor = new THREE.Vector3();
                      for (let ai = 0; ai < pAttr.count; ai++) anchor.set(anchor.x + pAttr.getX(ai), anchor.y + pAttr.getY(ai), anchor.z + pAttr.getZ(ai));
                      anchor.multiplyScalar(1 / pAttr.count);
                      gdtTekYuzey = { etiket: g.etiket, sapma: g.sapma, tip: gdtTip, anchor };
                      this.gdtSembolGoster(gdtTip, g.sapma, anchor);
                    } else {
                      this.gdtAnnotator.temizle();
                    }
                  } catch { }

                  // 🎯 v3.2.9: Gerçek değerleri sakla (yuvarlanmamış), gösterimde yuvarla
                  this.sonuc = {
                    yuzey: {
                      alan: (alan != null && !isNaN(alan)) ? alan : 0,  // 🎯 Gerçek sayı olarak sakla
                      agirlikMerkezi: agirlikMerkeziKoor,
                      miDuzlemsel: miYuzeyDuzlemsel(yuzeyGeometri),
                      duzlem: duzlemKoor,
                      tipi: yuzeyTipi.tip,
                      tipiBilgisi: yuzeyTipi.icerik,
                      gdt: gdtTekYuzey,
                    }
                  }

                  // 🎯 ALAN 3D: yüzey alanını sahnede merkeze etiketle (dialogdaki değer 3D'de de görünsün)
                  try {
                    const _pa = (yuzeyGeometri as any).attributes?.position;
                    if (_pa && alan && alan > 0) {
                      const _mc = new THREE.Vector3();
                      for (let ai = 0; ai < _pa.count; ai++) _mc.set(_mc.x + _pa.getX(ai), _mc.y + _pa.getY(ai), _mc.z + _pa.getZ(ai));
                      _mc.multiplyScalar(1 / _pa.count);
                      this.alanEtiketGoster(_mc, alan);
                    }
                  } catch { }

                  // ✅ Yüzey ölçümü gösterimi - Tip ve alana göre
                  // 🎯 v3.2.11: "silindir" ve "kure" tipleri de "egri" olarak işlenir
                  if (yuzeyTipi.tip === "egri" || yuzeyTipi.tip === "silindir" || yuzeyTipi.tip === "kure") {
                    // ✅ DÜZELTME v13: Tüm geometri kullanılır + 95. yüzdelik yarıçap
                    const posAttr = yuzeyGeometri.attributes.position;
                    const normalAttr = yuzeyGeometri.attributes.normal;
                    const tiklanaNokta = kesisim.point.clone();

                    if (!posAttr || posAttr.count < 6) {
                      this.CD.detectChanges();
                      return;
                    }

                    // ✅ ADIM 1: Tüm vertex ve normalleri topla (🚀 ZERO-ALLOCATION: Float32Array kullan)
                    const vertexCount = posAttr.count;
                    const posArray = posAttr.array as Float32Array;
                    const normalArray = normalAttr ? normalAttr.array as Float32Array : null;

                    // ✅ ADIM 2: Silindir eksenini bul (Normal cross product) - 🚀 ZERO-ALLOCATION
                    const axis = KumpasComponent._tempAxis.set(0, 0, 1);
                    let axisFound = false;

                    if (normalArray && vertexCount >= 2) {
                      const bestCross = KumpasComponent._tempVec1.set(0, 0, 0);
                      const n1 = KumpasComponent._tempVec2;
                      const n2 = KumpasComponent._tempVec3;
                      const cross = KumpasComponent._tempCross;
                      let maxCrossLen = 0;

                      const limit = Math.min(vertexCount, 50);
                      for (let i = 0; i < limit; i++) {
                        const i3 = i * 3;
                        n1.set(normalArray[i3], normalArray[i3 + 1], normalArray[i3 + 2]).normalize();
                        for (let j = i + 1; j < limit; j++) {
                          const j3 = j * 3;
                          n2.set(normalArray[j3], normalArray[j3 + 1], normalArray[j3 + 2]).normalize();
                          cross.crossVectors(n1, n2);
                          const len = cross.length();
                          if (len > maxCrossLen) {
                            maxCrossLen = len;
                            bestCross.copy(cross);
                          }
                        }
                      }

                      if (maxCrossLen > 0.01) {
                        axis.copy(bestCross).normalize();
                        axisFound = true;
                      }
                    }

                    // PCA fallback - 🚀 ZERO-ALLOCATION: Float32Array kullan
                    if (!axisFound) {
                      const centroidTemp = KumpasComponent._tempCentroid.set(0, 0, 0);
                      for (let i = 0; i < vertexCount; i++) {
                        const i3 = i * 3;
                        centroidTemp.x += posArray[i3];
                        centroidTemp.y += posArray[i3 + 1];
                        centroidTemp.z += posArray[i3 + 2];
                      }
                      centroidTemp.divideScalar(vertexCount);

                      let cxx = 0, cyy = 0, czz = 0, cxy = 0, cxz = 0, cyz = 0;
                      for (let i = 0; i < vertexCount; i++) {
                        const i3 = i * 3;
                        const dx = posArray[i3] - centroidTemp.x;
                        const dy = posArray[i3 + 1] - centroidTemp.y;
                        const dz = posArray[i3 + 2] - centroidTemp.z;
                        cxx += dx * dx; cyy += dy * dy; czz += dz * dz;
                        cxy += dx * dy; cxz += dx * dz; cyz += dy * dz;
                      }

                      const newAxis = KumpasComponent._tempVec1;
                      for (let iter = 0; iter < 30; iter++) {
                        newAxis.set(
                          cxx * axis.x + cxy * axis.y + cxz * axis.z,
                          cxy * axis.x + cyy * axis.y + cyz * axis.z,
                          cxz * axis.x + cyz * axis.y + czz * axis.z
                        );
                        if (newAxis.length() > 0.001) {
                          newAxis.normalize();
                          axis.copy(newAxis);
                        }
                      }
                    }

                    // ✅ ADIM 3: Basis vektörleri - 🚀 ZERO-ALLOCATION
                    const uVec = KumpasComponent._tempUVec;
                    const vVec = KumpasComponent._tempVVec;
                    if (Math.abs(axis.x) < 0.9) {
                      uVec.set(1, 0, 0);
                      uVec.crossVectors(axis, uVec).normalize();
                    } else {
                      uVec.set(0, 1, 0);
                      uVec.crossVectors(axis, uVec).normalize();
                    }
                    vVec.crossVectors(axis, uVec).normalize();

                    // ✅ ADIM 4: Centroid ve 2D projeksiyon - 🚀 ZERO-ALLOCATION
                    const centroid = KumpasComponent._tempCentroid.set(0, 0, 0);
                    for (let i = 0; i < vertexCount; i++) {
                      const i3 = i * 3;
                      centroid.x += posArray[i3];
                      centroid.y += posArray[i3 + 1];
                      centroid.z += posArray[i3 + 2];
                    }
                    centroid.divideScalar(vertexCount);

                    // 🚀 ZERO-ALLOCATION: projected2D dizisi hala gerekli (farklı boyutta olabilir)
                    // Ancak loop içinde clone() kullanmıyoruz
                    const projected2D: { x: number; y: number }[] = [];
                    const rel = KumpasComponent._tempRel;
                    for (let i = 0; i < vertexCount; i++) {
                      const i3 = i * 3;
                      rel.set(posArray[i3] - centroid.x, posArray[i3 + 1] - centroid.y, posArray[i3 + 2] - centroid.z);
                      // Eksene dik bileşeni bul
                      const axisProj = rel.dot(axis);
                      rel.x -= axis.x * axisProj;
                      rel.y -= axis.y * axisProj;
                      rel.z -= axis.z * axisProj;
                      projected2D.push({
                        x: rel.dot(uVec),
                        y: rel.dot(vVec)
                      });
                    }

                    // ✅ ADIM 5: 2D Circle Fitting (Algebraic Kasa + Geometric Refinement)
                    // Önce Kasa metodu ile başlangıç tahmini
                    let sumX = 0, sumY = 0, sumX2 = 0, sumY2 = 0, sumXY = 0;
                    let sumX3 = 0, sumY3 = 0, sumX2Y = 0, sumXY2 = 0;

                    projected2D.forEach(p => {
                      sumX += p.x; sumY += p.y;
                      sumX2 += p.x * p.x; sumY2 += p.y * p.y;
                      sumXY += p.x * p.y;
                      sumX3 += p.x ** 3; sumY3 += p.y ** 3;
                      sumX2Y += p.x * p.x * p.y; sumXY2 += p.x * p.y * p.y;
                    });

                    const N = projected2D.length;
                    const A = N * sumX2 - sumX * sumX;
                    const B = N * sumXY - sumX * sumY;
                    const C = N * sumY2 - sumY * sumY;
                    const D = 0.5 * (N * sumX3 + N * sumXY2 - sumX * sumX2 - sumX * sumY2);
                    const E = 0.5 * (N * sumX2Y + N * sumY3 - sumY * sumX2 - sumY * sumY2);

                    const denom = A * C - B * B;
                    if (Math.abs(denom) < 1e-10) {
                      this.CD.detectChanges();
                      return;
                    }

                    let cx2d = (D * C - B * E) / denom;
                    let cy2d = (A * E - B * D) / denom;

                    // ✅ Geometrik refinement (Gauss-Newton iterasyonu)
                    // Kasa'dan sonra daha hassas merkez bul
                    for (let iter = 0; iter < 10; iter++) {
                      // Mevcut yarıçap
                      let rSum = 0;
                      projected2D.forEach(p => {
                        rSum += Math.sqrt((p.x - cx2d) ** 2 + (p.y - cy2d) ** 2);
                      });
                      const rMean = rSum / N;

                      // Gradient hesapla (sum of (ri - rMean)^2 minimize et)
                      let gradX = 0, gradY = 0;
                      projected2D.forEach(p => {
                        const dx = p.x - cx2d;
                        const dy = p.y - cy2d;
                        const ri = Math.sqrt(dx * dx + dy * dy);
                        if (ri > 0.001) {
                          const err = ri - rMean;
                          gradX += err * (-dx / ri);
                          gradY += err * (-dy / ri);
                        }
                      });

                      // Gradient descent step
                      const step = 0.5 / N;
                      const newCx = cx2d - step * gradX;
                      const newCy = cy2d - step * gradY;

                      // Converge check
                      if (Math.abs(newCx - cx2d) < 1e-8 && Math.abs(newCy - cy2d) < 1e-8) break;
                      cx2d = newCx;
                      cy2d = newCy;
                    }

                    // ✅ Final yarıçap: Median kullan (outlier'lara dayanıklı)
                    const allRadii: number[] = [];
                    projected2D.forEach(p => {
                      const r = Math.sqrt((p.x - cx2d) ** 2 + (p.y - cy2d) ** 2);
                      allRadii.push(r);
                    });
                    allRadii.sort((a, b) => a - b);

                    // Median (ortanca) değer
                    const medianIndex = Math.floor(allRadii.length / 2);
                    const gercekYaricap = allRadii[medianIndex];

                    // ✅ ADIM 6: 3D merkez hesapla - 🚀 ZERO-ALLOCATION (hesaplama)
                    // Merkez = centroid + uVec*cx2d + vVec*cy2d
                    const gercekMerkez = KumpasComponent._tempGercekMerkez
                      .copy(centroid)
                      .addScaledVector(uVec, cx2d)
                      .addScaledVector(vVec, cy2d);

                    // Geçerlilik kontrolü
                    if (isNaN(gercekYaricap) || gercekYaricap < 0.1 || gercekYaricap > 100000) {
                      this.CD.detectChanges();
                      return;
                    }

                    // ✅ ADIM 7: Çap çizgisi yönünü belirle - 🚀 ZERO-ALLOCATION
                    // Tıklanan noktadan merkeze yön (eksene dik bileşen)
                    const tikRel = KumpasComponent._tempTikRel.copy(tiklanaNokta).sub(gercekMerkez);
                    const tikRelAxisProj = tikRel.dot(axis);
                    tikRel.addScaledVector(axis, -tikRelAxisProj);

                    const capYonu = KumpasComponent._tempCapYonu;
                    if (tikRel.length() > gercekYaricap * 0.1) {
                      capYonu.copy(tikRel).normalize();
                    } else {
                      capYonu.copy(uVec); // Fallback
                    }

                    // ✅ ADIM 8: Çap uç noktalarını hesapla ve çiz - 🚀 ZERO-ALLOCATION (hesaplama)
                    // Tıklanan noktanın eksen üzerindeki pozisyonunda çap çiz
                    const tikAxisPos = KumpasComponent._tempVec1.copy(tiklanaNokta).sub(gercekMerkez).dot(axis);
                    const capMerkez = KumpasComponent._tempCapMerkez.copy(gercekMerkez).addScaledVector(axis, tikAxisPos);

                    // 🔴 NOT: gosterimKayniyaEkle fonksiyonuna geçirilen değerler klonlanmalı
                    // çünkü bu fonksiyon değerleri saklayabilir
                    const capUc1 = new THREE.Vector3().copy(capMerkez).addScaledVector(capYonu, gercekYaricap);
                    const capUc2 = new THREE.Vector3().copy(capMerkez).addScaledVector(capYonu, -gercekYaricap);

                    const gercekCap = gercekYaricap * 2;
                    const capLabel = `Ø ${gercekCap.toFixed(2)} mm`;

                    // 🎯 v3.2.8: Çap/yarıçap bilgisini this.sonuc'a kaydet (kalıcı ölçüm için kritik!)
                    if (!this.sonuc.cizgi) {
                      this.sonuc.cizgi = { uzunluk: 0 };
                    }
                    this.sonuc.cizgi.cember = {
                      cap: gercekCap,
                      yariCap: gercekYaricap,
                      merkez: gercekMerkez.clone(),
                      nokta1: capUc1.clone(),
                      nokta2: capUc2.clone()
                    };

                    this.gosterimKayniyaEkle(capUc1, capUc2, capLabel, false, 0, undefined, 'cap');
                  } else if (yuzeyTipi.tip === "duzlem" || yuzeyTipi.tip === "duzlemsel" || yuzeyTipi.tip === "bilinmeyen" || yuzeyTipi.tip === "kompleks") {
                    // 🎯 v3.2.11 FIX: yuzeyTipiTespit() "duzlem" ve "kompleks" döndürür
                    // Düzlem veya kompleks yüzey - Alan göster
                    const merkezNokta = agirlikMerkezi(yuzeyGeometri);
                    const alanLabel = `${alan.toFixed(this.alanBas)} mm²`;
                    this.gosterimKayniyaEkle(merkezNokta, merkezNokta, alanLabel, false, 0, undefined, 'alan');
                    // console.log('[Kumpas] v3.2.11 Düz/Kompleks yüzey algılandı:', yuzeyTipi.tip, 'Alan:', alan);
                  }

                  this.CD.detectChanges();
                } else {
                  this.sonuc = null;
                  this.CD.detectChanges();
                }
              }
            } else
              /** NOT:  Seçilen Çizgi İse */
              if (kesisim?.object?.type == 'LineSegments' ||
                  kesisim?.object?.type == 'LineSegments2' ||
                  kesisim?.object?.type == 'LineGeometry' ||
                  kesisim?.object?.type == 'Line2' ||
                  (kesisim?.object?.type === 'FikoCizimYalin' && (kesisim as any)?.altObje &&
                   ((kesisim as any).altObje.type === 'LineSegments' ||
                    (kesisim as any).altObje.type === 'LineSegments2' ||
                    (kesisim as any).altObje.type === 'Line2'))) {

                // ✅ FIX: kesisim objesinden al, grupSecimler.children[0]'dan değil!
                // Böylece her seçim farklı çizgi/ölçü döndürür
                let secilenOrijinal: THREE.LineSegments = (kesisim?.object) as THREE.LineSegments;
                let secilen: THREE.LineSegments = ((kesisim as any)?.altObje || kesisim?.object) as THREE.LineSegments;

                if (secilen && secilen.type == "Line2" && secilen.userData['lineS']) {
                    secilen = secilen.userData['lineS'];
                }

                let cizimKok: any = null; // Transform için kök obje

                // Doğru objeyi ve kök objeyi belirle
                if ((kesisim as any).object?.type === "FikoCizimYalin" && (kesisim as any).altObje) {
                  // ✅ FIX: Orijinal objeyi saklayalım çember hesaplaması için
                  secilenOrijinal = (kesisim as any).object as any;
                  secilen = (kesisim as any).altObje;
                  cizimKok = (kesisim as any).object;
                } else {
                  secilen = kesisim.object as THREE.LineSegments;
                  secilenOrijinal = kesisim.object as THREE.LineSegments;
                  // Parent'ı bul (transform için). Modellenen gövde: govdeMesh edgesGeom taşır → onu tercih et
                  // (doğru matrixWorld). FikoGrup'tan önce govdeMesh yakalanır → tutarlı transform.
                  let parent = secilen.parent;
                  while (parent && parent.type !== "FikoCizimYalin" && parent.type !== "FikoGrup" && !(parent as any).edgesGeom) {
                    parent = parent.parent;
                  }
                  if (parent && (parent.type === "FikoCizimYalin" || parent.type === "FikoGrup" || (parent as any).edgesGeom)) {
                    cizimKok = parent;
                  }

                  // Eğer LineSegments2 ise ve FikoCizimYalin içindeyse, grup bilgisini oradan al
                  if (secilen.type === "LineSegments2" && cizimKok && cizimKok.type === "FikoCizimYalin") {
                    // Grup bilgisini FikoCizimYalin'dan alacak şekilde ayarla
                  }
                }

                if (secilen.type == "Line2")
                  secilen = secilen.userData['lineS'];

                // ✅ FIX: secilenOrijinal da Line2 ise fallback'e çevir
                if (secilenOrijinal.type == "Line2")
                  secilenOrijinal = secilenOrijinal.userData['lineS'];

                if (!secilen || !secilen.geometry) {
                  this.sonucTipi = null;
                  this.sonuc = null;
                } else {
                  this.sonucTipi = 'Çizgi';
                  let cizgiGeometri = secilen.geometry.clone();

                  // ✅ GRUP BAZLI SEÇİM - TÜRLER İÇİN
                  let buffers = null;
                  let useParentGeom = false;

                  // ✅ FIX: LineSegments2 için FikoCizimYalin'ın edgesGeom'unu kullan (meshGeom değil!)
                  if (secilen.type === "LineSegments2" && cizimKok && cizimKok.type === "FikoCizimYalin" && cizimKok.edgesGeom) {
                    buffers = cizimKok.edgesGeom.attributes;
                    useParentGeom = true;
                  } else if (kesisim.index !== undefined && secilen.geometry.hasAttribute("grup")) {
                    buffers = secilen.geometry.attributes;
                  }

                  // ✅ FIX: kesisim.index undefined olabilir (LineSegments2), pointOnLine'ı kullan
                  let selectedIndex = kesisim.index;
                  let selectedSegmentIndex = -1;

                  if (selectedIndex === undefined && kesisim.pointOnLine && buffers) {
                    // pointOnLine'dan en yakın segment'i bul (LineSegments2 için)
                    const posAttr = buffers["position"];
                    if (posAttr) {
                      let minDist = Infinity;
                      // LineSegments: her 2 vertex bir segment oluşturur
                      for (let i = 0; i < posAttr.count - 1; i += 2) {
                        // Segment: (v[i], v[i+1])
                        const v1x = posAttr.getX(i);
                        const v1y = posAttr.getY(i);
                        const v1z = posAttr.getZ(i);
                        const v2x = posAttr.getX(i + 1);
                        const v2y = posAttr.getY(i + 1);
                        const v2z = posAttr.getZ(i + 1);

                        // pointOnLine'dan segment'e uzaklık
                        const px = kesisim.pointOnLine.x;
                        const py = kesisim.pointOnLine.y;
                        const pz = kesisim.pointOnLine.z;

                        // Point to line segment distance
                        const dx = v2x - v1x;
                        const dy = v2y - v1y;
                        const dz = v2z - v1z;
                        const t = Math.max(0, Math.min(1,
                          ((px - v1x) * dx + (py - v1y) * dy + (pz - v1z) * dz) / (dx * dx + dy * dy + dz * dz)
                        ));
                        const closestX = v1x + t * dx;
                        const closestY = v1y + t * dy;
                        const closestZ = v1z + t * dz;
                        const dist = Math.pow(px - closestX, 2) + Math.pow(py - closestY, 2) + Math.pow(pz - closestZ, 2);

                        if (dist < minDist) {
                          minDist = dist;
                          selectedIndex = i; // Segment'in ilk vertex'i
                          selectedSegmentIndex = i / 2; // Segment index
                        }
                      }
                    }
                  }

                  // ✅ FIX: Highlight geometrisini DAIMA kullan (en doğru!)
                  if (this.fikoSecim1.sonSecilenHighlightGeometri) {
                    cizgiGeometri = this.fikoSecim1.sonSecilenHighlightGeometri.clone();

                    // Transform uygula
                    if (cizimKok && cizimKok.matrixWorld) {
                      cizgiGeometri.applyMatrix4(cizimKok.matrixWorld);
                    }
                  } else if (buffers && selectedIndex !== undefined && buffers["grup"]) {
                    const index = selectedIndex;

                    // ✅ FIX: LineSegments2 için segment'in grubunu al
                    let selectedGrp: number;
                    if (selectedSegmentIndex >= 0 && buffers["grup"].count > selectedSegmentIndex) {
                      // Segment index'den doğrudan grup al
                      selectedGrp = buffers["grup"].getZ(selectedSegmentIndex);
                    } else {
                      // Fallback: vertex index'ten grup al
                      selectedGrp = buffers["grup"].getZ(index);
                    }

                    // Aynı gruba ait tüm vertex'leri topla
                    let geom = [];
                    let vertexSayisi = 0;

                    // Tüm vertex'leri tara ve aynı gruptakileri topla
                    for (let i = 0; i < buffers["position"].count; i++) {
                      const currentGrp = buffers["grup"].getZ(i);

                      if (currentGrp === selectedGrp) {
                        geom.push(buffers["position"].getX(i));
                        geom.push(buffers["position"].getY(i));
                        geom.push(buffers["position"].getZ(i));
                        vertexSayisi++;
                      }
                    }

                    if (geom.length > 0) {
                      cizgiGeometri = new THREE.BufferGeometry();
                      cizgiGeometri.setAttribute('position', new THREE.Float32BufferAttribute(geom, 3));

                      // ✅ FIX: Transform'u geometri'ye doğru uygula
                      if (cizimKok) {
                        cizgiGeometri.applyMatrix4(cizimKok.matrixWorld);
                      }
                    }
                  } else {
                    // ✅ FIX: Highlight geometrisini kullan (en doğru seçim)
                    if (this.fikoSecim1.sonSecilenHighlightGeometri) {
                      cizgiGeometri = this.fikoSecim1.sonSecilenHighlightGeometri.clone();

                      // Transform uygula
                      if (cizimKok && cizimKok.matrixWorld) {
                        cizgiGeometri.applyMatrix4(cizimKok.matrixWorld);
                      }
                    } else if (secilen.type === "LineSegments2" && useParentGeom && cizimKok && cizimKok.edgesGeom) {
                      const edgesGeom = cizimKok.edgesGeom;
                      if (edgesGeom.hasAttribute("position")) {
                        let geomToUse = edgesGeom;
                        if (geomToUse.index !== null) {
                          geomToUse = geomToUse.toNonIndexed();
                        }
                        const positions = geomToUse.attributes.position.array;

                        cizgiGeometri = new THREE.BufferGeometry();
                        cizgiGeometri.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

                        if (cizimKok) {
                          cizgiGeometri.applyMatrix4(cizimKok.matrixWorld);
                        }
                      }
                    } else if (secilen.geometry.hasAttribute("position")) {
                      // Normal fallback - secilen.geometry kullan
                      let geomToUse = secilen.geometry;

                      // ✅ FIX: Indexed geometry'yi non-indexed'a çevir
                      if (geomToUse.index !== null) {
                        geomToUse = geomToUse.toNonIndexed();
                      }

                      const positions = geomToUse.attributes.position.array;

                      cizgiGeometri = new THREE.BufferGeometry();
                      cizgiGeometri.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

                      // ✅ FIX: Transform'u geometri'ye doğru uygula
                      if (cizimKok) {
                        cizgiGeometri.applyMatrix4(cizimKok.matrixWorld);
                      }
                    }
                  }

                  /** NOT: Eksene Göre */
                  let cizgiYon: FikoVektor3D = alCizgiYon(cizgiGeometri);
                  if (cizgiYon) {
                    cizgiYon.applyQuaternion((this.kumpasEksen || new FikoEksen()).quaternion.clone().invert());
                  }

                  let uzunluk = alCizgiUzunluk(cizgiGeometri);

                  this.sonuc = {
                    cizgi: {
                      uzunluk: uzunluk,
                      miDuzlemsel: miCizgiYon(cizgiGeometri),
                      yon: cizgiYon ? cizgiYon.normalize() : null,
                    }
                  }

                  // 🎯 BREP-FIRST: STEP dosyasından gelen kesin BREP verisi varsa onu kullan
                  // miCember() yaklaşık değer üretir, BREP verisi kesindir (OpenCascade'den)
                  let cemberMi = false;
                  const brepBilgi = this.fikoSecim1?.sonSecilenEdgeBrepBilgi;

                  if (brepBilgi && (brepBilgi.curveType === 'CIRCLE' || brepBilgi.curveType === 'ELLIPSE')
                      && brepBilgi.radius && brepBilgi.radius > 0.01 && brepBilgi.center) {
                    // 🎯 BREP KESIN DEĞER: OpenCascade'den gelen radius/center/axis
                    cemberMi = true;
                    const brepYaricap = brepBilgi.radius;
                    const brepCap = brepYaricap * 2;

                    // Merkezi world space'e çevir
                    const brepMerkezLocal = new THREE.Vector3(
                      brepBilgi.center.x, brepBilgi.center.y, brepBilgi.center.z
                    );
                    // cizimKok matrixWorld ile world space'e çevir
                    const brepMerkezWorld = brepMerkezLocal.clone();
                    if (cizimKok && cizimKok.matrixWorld) {
                      brepMerkezWorld.applyMatrix4(cizimKok.matrixWorld);
                    }

                    // Eksene göre düzeltilmiş merkez
                    let cemMerkez: FikoVektor3D = brepMerkezWorld.clone() as any;
                    cemMerkez.sub((this.kumpasEksen || new FikoEksen()).merkez);
                    cemMerkez.applyQuaternion((this.kumpasEksen || new FikoEksen()).quaternion.clone().invert());

                    this.sonuc.cizgi.cember = {
                      yariCap: brepYaricap,
                      cap: brepCap,
                      merkez: cemMerkez,
                      aci: 360, // Tam daire (BREP CIRCLE tipi)
                      brepKesin: true // 🎯 BREP kaynaklı kesin değer göstergesi
                    };

                    // Çap uç noktalarını hesapla (highlight geometry'sinden yön al)
                    const posAttrBrep = cizgiGeometri.attributes.position;
                    let brepCapYonu = new THREE.Vector3(1, 0, 0);
                    if (posAttrBrep && posAttrBrep.count >= 2) {
                      const ilkP = new THREE.Vector3(posAttrBrep.getX(0), posAttrBrep.getY(0), posAttrBrep.getZ(0));
                      brepCapYonu.subVectors(ilkP, brepMerkezWorld).normalize();
                      if (brepCapYonu.length() < 0.01) brepCapYonu.set(1, 0, 0);
                    }

                    const capUc1 = brepMerkezWorld.clone().addScaledVector(brepCapYonu, brepYaricap);
                    const capUc2 = brepMerkezWorld.clone().addScaledVector(brepCapYonu, -brepYaricap);

                    // Eksene göre nokta1 ve nokta2
                    let nokta1Brep = capUc1.clone() as any;
                    nokta1Brep.sub((this.kumpasEksen || new FikoEksen()).merkez);
                    nokta1Brep.applyQuaternion((this.kumpasEksen || new FikoEksen()).quaternion.clone().invert());
                    this.sonuc.cizgi.cember.nokta1 = nokta1Brep;

                    let nokta2Brep = capUc2.clone() as any;
                    nokta2Brep.sub((this.kumpasEksen || new FikoEksen()).merkez);
                    nokta2Brep.applyQuaternion((this.kumpasEksen || new FikoEksen()).quaternion.clone().invert());
                    this.sonuc.cizgi.cember.nokta2 = nokta2Brep;

                    // 3D viewport'ta çap ölçümü göster
                    const capLabel = `Ø ${brepCap.toFixed(2)} mm`;
                    this.gosterimKayniyaEkle(capUc1, capUc2, capLabel, false, 0, brepMerkezWorld, 'cap');
                  }

                  // 🔄 FALLBACK: BREP verisi yoksa geometry analizi ile hesapla
                  if (!cemberMi) {
                    let cemberGeometri = cizgiGeometri.clone();
                    let miCem = miCember(cemberGeometri);

                    if (miCem && miCem.yaricap && !isNaN(miCem.yaricap)) {
                      const avgDeviation = miCem.ortalamaSapma ?? 0;
                      const deviationRatio = (avgDeviation / miCem.yaricap) * 100;

                      if (deviationRatio > 50 || miCem.yaricap < 0.1) {
                        this.sonuc.cizgi.cember = null;
                        cemberMi = false;
                      } else {
                        cemberMi = true;
                        this.sonuc.cizgi.cember = {};
                        this.sonuc.cizgi.cember.yariCap = miCem.yaricap;
                        this.sonuc.cizgi.cember.cap = miCem.cap;

                        /** NOT: Eksene Göre */
                        let cemMerkez: FikoVektor3D = miCem.merkez.clone();
                        cemMerkez.sub((this.kumpasEksen || new FikoEksen()).merkez);
                        cemMerkez.applyQuaternion((this.kumpasEksen || new FikoEksen()).quaternion.clone().invert());

                        this.sonuc.cizgi.cember.merkez = cemMerkez;
                        this.sonuc.cizgi.cember.aci = sokToleransa(miCem.aci, 0.010);

                        /** NOT: Eksene Göre */
                        let nokta1: FikoVektor3D = miCem.nokta1.clone();
                        nokta1.sub((this.kumpasEksen || new FikoEksen()).merkez);
                        nokta1.applyQuaternion((this.kumpasEksen || new FikoEksen()).quaternion.clone().invert());
                        this.sonuc.cizgi.cember.nokta1 = nokta1;

                        /** NOT: Eksene Göre */
                        let nokta2: FikoVektor3D = miCem.nokta2.clone();
                        nokta2.sub((this.kumpasEksen || new FikoEksen()).merkez);
                        nokta2.applyQuaternion((this.kumpasEksen || new FikoEksen()).quaternion.clone().invert());
                        this.sonuc.cizgi.cember.nokta2 = nokta2;

                        // Çember ölçümü gösterimi - ÇAP göster
                        const merkezWorld = miCem.merkez.clone();
                        const posAttrCember = cemberGeometri.attributes.position;
                        let enUzakNokta = miCem.nokta1.clone();
                        let maxMesafe = 0;

                        if (posAttrCember && posAttrCember.count > 0) {
                          for (let i = 0; i < posAttrCember.count; i++) {
                            const nokta = new THREE.Vector3(
                              posAttrCember.getX(i),
                              posAttrCember.getY(i),
                              posAttrCember.getZ(i)
                            );
                            const mesafe = merkezWorld.distanceTo(nokta);
                            if (mesafe > maxMesafe) {
                              maxMesafe = mesafe;
                              enUzakNokta.copy(nokta);
                            }
                          }
                        }

                        const gercekYaricap = maxMesafe > 0 ? maxMesafe : merkezWorld.distanceTo(miCem.nokta1);
                        const yariCapYonu = new THREE.Vector3().subVectors(enUzakNokta, merkezWorld).normalize();
                        const capUc1 = merkezWorld.clone().add(yariCapYonu.clone().multiplyScalar(gercekYaricap));
                        const capUc2 = merkezWorld.clone().sub(yariCapYonu.clone().multiplyScalar(gercekYaricap));

                        const gercekCap = gercekYaricap * 2;
                        const capLabel = `Ø ${gercekCap.toFixed(2)} mm`;
                        this.gosterimKayniyaEkle(capUc1, capUc2, capLabel, false, 0, merkezWorld, 'cap');
                      }
                    } else {
                      this.sonuc.cizgi.cember = null;
                      cemberMi = false;
                    }
                  }

                  // ✅ Çizgi ölçümü gösterimi ekle - Sadece çember değilse
                  if (!cemberMi) {
                    const posAttr = cizgiGeometri.attributes.position;
                    if (posAttr && posAttr.count >= 2) {
                      const ilkNokta = new THREE.Vector3(
                        posAttr.getX(0),
                        posAttr.getY(0),
                        posAttr.getZ(0)
                      );
                      const sonNokta = new THREE.Vector3(
                        posAttr.getX(posAttr.count - 1),
                        posAttr.getY(posAttr.count - 1),
                        posAttr.getZ(posAttr.count - 1)
                      );

                      const label = `${uzunluk.toFixed(2)} mm`;
                      this.gosterimKayniyaEkle(ilkNokta, sonNokta, label, false, 0, undefined, 'uzunluk');
                    }
                  }

                  // ✅ ADD: Çizgi Eğriliği Tespiti
                  const egriliBilgi = cizgiEgrilikiTespit(cizgiGeometri);
                  if (egriliBilgi) {
                    this.sonuc.cizgi.egriliBilgi = egriliBilgi;
                  }
                }

                this.CD.detectChanges();

              } else {
                this.sonucTipi = null;
                this.sonuc = null;
                this.CD.detectChanges();
              }

        // 🎯 SADECE HOVER HIGHLIGHT TEMİZLE - Seçim highlight'ı kalmalı
        if (this.fikoSecim1.grupSecimlerUzerinde) {
          this.fikoSecim1.grupSecimlerUzerinde.clear();
        }
        this.anaGosterim.ciz();
      })
    );
  }

  tiklandiEksenSil() {
    this.sonuc = null;

    if (this.fikoRaycastEksenSec) {
      this.fikoRaycastEksenSec.bitir();
      this.fikoRaycastEksenSec.dispose();
      this.fikoRaycastEksenSec = null;
    }

    this.kumpasEksen = null;
    this.CD.detectChanges();
  }

  fikoRaycastEksenSec: FikoRaycastCPUYardimci;
  tiklandiEksenSec() {
    this.sonuc = null;

    if (this.fikoRaycastEksenSec) {
      this.fikoRaycastEksenSec.bitir();
      this.fikoRaycastEksenSec.dispose();
      this.fikoRaycastEksenSec = null;
    }

    this.fikoSecim1.odakDuraklat = true;

    this.fikoRaycastEksenSec = new FikoRaycastCPUYardimci(
      this.anaGosterim,
      null,//this.anaGosterim.sahneler[0].children,
      ["FikoEksen"],
      true
    );

    let subsUzerinde = this.fikoRaycastEksenSec.uzerinde.subscribe((eksen) => {
    });

    let subsTiklandi = this.fikoRaycastEksenSec.tiklandi.subscribe((eksen) => {
      if (eksen) {
        this.kumpasEksen = eksen.object as FikoEksen;
        this.CD.detectChanges();
        this.fikoRaycastEksenSec.bitir();
        this.fikoRaycastEksenSec.dispose();
        this.fikoRaycastEksenSec = null;
        this.fikoSecim1.odakDuraklat = true;
      }
    });

    this.fikoRaycastEksenSec.baslat();
  }

  /**
   * Sonuç gösterimini formatla (koordinat basamağı uygulanır)
   */
  formatKoordinat(deger: number): string {
    if (deger == null || isNaN(deger)) return '0';
    return deger.toFixed(this.koordinatBas);
  }

  /**
   * Alan gösterimini formatla
   */
  formatAlan(deger: number): string {
    if (deger == null || isNaN(deger)) return '0';
    return deger.toFixed(this.alanBas);
  }

  /**
   * Gösterim ekleme - Ölçüm çizgisi ekle
   * 🚀 OPTIMIZE: Sadece ölçüm çizgisini temizle (hover/kalıcı noktaları değil)
   * 🎯 YENİ: useNewDimensionSystem = true ise DimensionIntegrationService kullanır
   */
  private gosterimKayniyaEkle(
    nokta1: THREE.Vector3,
    nokta2: THREE.Vector3,
    label: string,
    isArc: boolean = false,
    aci: number = 0,
    merkezNoktasi?: THREE.Vector3,
    olcumTuru: OlcumTuru = 'mesafe'
  ): void {
    // Sahne kontrol et
    if (!this.anaGosterim || !this.anaGosterim.sahneler[0]) {
      return;
    }

    const sahneFiko = this.anaGosterim.sahneler[0] as any;
    const sahneSonucu = sahneFiko.sahne as any;

    // 🎯 YENİ DIMENSION SİSTEMİ: Aktifse yeni sistemi kullan
    if (this.useNewDimensionSystem && this.dimensionService) {
      // Yeni sistem kullanılacaksa eski instance'ı temizle
      if (this.fikoOlcumCizgisi) {
        if (this.fikoOlcumCizgisi.parent) {
          this.fikoOlcumCizgisi.parent.remove(this.fikoOlcumCizgisi);
        }
        this.fikoOlcumCizgisi.dispose();
        this.fikoOlcumCizgisi = null;
      }
      this.gosterimYeniSistemIle(nokta1, nokta2, label, olcumTuru, merkezNoktasi);
      return;
    }

    // ===== ESKİ SİSTEM (FikoOlcumCizgisi) =====
    const uzunluk = nokta1.distanceTo(nokta2);

    // ⚡ PERFORMANS: Mevcut instance'ı yeniden kullan (aynı ölçüm türü ise)
    // destroy+recreate yerine noktaGuncelle() ile in-place güncelleme
    if (this.fikoOlcumCizgisi && this.fikoOlcumCizgisi.olcumTuru === olcumTuru && !isArc) {
      this.fikoOlcumCizgisi.noktaGuncelle(nokta1, nokta2, uzunluk, label, merkezNoktasi);

      if (this.anaGosterim) {
        this.anaGosterim.ciz();
      }
      return;
    }

    // Farklı tür veya ilk oluşturma: eski instance'ı temizle
    if (this.fikoOlcumCizgisi) {
      if (this.fikoOlcumCizgisi.parent) {
        this.fikoOlcumCizgisi.parent.remove(this.fikoOlcumCizgisi);
      }
      this.fikoOlcumCizgisi.dispose();
      this.fikoOlcumCizgisi = null;
    }

    const cizgiRenk = parseInt(this.cizimRengi.replace('#', ''), 16);
    this.fikoOlcumCizgisi = new FikoOlcumCizgisi(
      sahneSonucu,
      `Ölçüm_${label}`,
      nokta1,
      nokta2,
      uzunluk,
      { cizgi: cizgiRenk, ok: cizgiRenk, yazi: this.cizimRengi },
      isArc,
      aci,
      merkezNoktasi,
      this.cizgiKalinligi,
      olcumTuru
    );

    // Yazı ekle - 🎯 v2.1.0: Koordinat için arkaplan ekle
    if (olcumTuru === 'koordinat') {
      // Koordinat için siyah arkaplan, beyaz yazı
      this.fikoOlcumCizgisi.yaziEkle(label, undefined, this.yaziBoyutu, '#FFFFFF', '#000000', 0.9);
    } else {
      this.fikoOlcumCizgisi.yaziEkle(label, undefined, this.yaziBoyutu, this.yaziRengi);
    }

    // 🔴 KRITIK FIX: .ekle() kullan -> hazirla() çağrılacak (cetvel ile aynı)
    sahneFiko.ekle(this.fikoOlcumCizgisi);

    // 🎯 SÜRÜKLEME DESTEĞİ: Ölçüm çizgisini sürüklenebilir yap
    if (this.anaGosterim?.sunucu && this.fikoOlcumCizgisi.draggable) {
      const canvas = this.anaGosterim.sunucu.domElement as HTMLCanvasElement;
      const kamera = this.anaGosterim.aktifKamera;
      if (canvas && kamera) {
        this.fikoOlcumCizgisi.suruklemeyiBaslat(canvas, kamera, sahneSonucu);
      }
    }

    if (this.anaGosterim) {
      this.anaGosterim.ciz();
    }
  }

  /**
   * 🎯 YENİ DIMENSION SİSTEMİ: Profesyonel CATIA/SolidWorks tarzı ölçüm çizgisi
   */
  private gosterimYeniSistemIle(
    nokta1: THREE.Vector3,
    nokta2: THREE.Vector3,
    label: string,
    olcumTuru: OlcumTuru,
    merkezNoktasi?: THREE.Vector3
  ): void {
    if (!this.dimensionService) return;

    // 🎯 v3.3.0: Koordinat türü bu fonksiyona gelmemeli - eski sistem kullanılmalı
    if (olcumTuru === 'koordinat') {
      return;
    }

    // Önceki dimension'ları temizle (yeni ölçüm için)
    // 🎯 Session bazlı temizleme - kalıcı ölçümler korunur
    const manager = this.dimensionService.getManager();
    if (manager?.clearSession && this.sessionId) {
      manager.clearSession(this.sessionId);
    } else {
      this.dimensionService.clearAll();
    }

    switch (olcumTuru) {
      case 'cap':
        // Çap ölçümü - 🎯 FikoOlcumCizgisi uyumluluğu: capUc1 ve capUc2 geçir
        // nokta1 = capUc1, nokta2 = capUc2 (çapın iki ucu)
        if (merkezNoktasi) {
          const yaricap = nokta1.distanceTo(merkezNoktasi);
          const capSonuc = this.dimensionService.createDiameterMeasurement(
            merkezNoktasi,
            yaricap,
            undefined,
            {
              birim: 'mm',
              hassasiyet: this.koordinatBas,
              capUc1: nokta1,  // 🎯 Çapın bir ucu
              capUc2: nokta2   // 🎯 Çapın diğer ucu
            }
          );
          // NOT: Çap ölçümü tek başına yeterli. Ayrıca R (radius) ölçümü EKLENMİYOR —
          // ikisi birden "çift çap" görünümü yaratıyordu (kullanıcı bildirimi).
        } else {
          // Merkez yoksa nokta1-nokta2 arasını çap olarak göster
          const merkez = nokta1.clone().add(nokta2).multiplyScalar(0.5);
          const yaricap = nokta1.distanceTo(nokta2) / 2;
          this.dimensionService.createDiameterMeasurement(
            merkez,
            yaricap,
            undefined,
            {
              birim: 'mm',
              hassasiyet: this.koordinatBas,
              capUc1: nokta1,
              capUc2: nokta2
            }
          );
        }
        break;

      case 'yaricap':
        // Yarıçap ölçümü - nokta1 = yüzey noktası (eğri yüzeylerde ok pozisyonu)
        if (merkezNoktasi) {
          const yaricap = nokta1.distanceTo(merkezNoktasi);
          this.dimensionService.createRadiusMeasurement(
            merkezNoktasi,
            yaricap,
            undefined,
            {
              birim: 'mm',
              hassasiyet: this.koordinatBas,
              surfacePoint: nokta1  // Gerçek yüzey noktası - ok burada olacak
            }
          );
        }
        break;

      case 'aci':
        // Açı ölçümü (ileride eklenebilir)
        // Şimdilik eski sisteme fallback
        this.useNewDimensionSystem = false;
        this.gosterimKayniyaEkle(nokta1, nokta2, label, true, 0, merkezNoktasi, olcumTuru);
        this.useNewDimensionSystem = true;
        break;

      // 🎯 v3.3.0: 'koordinat' case kaldırıldı - fonksiyon başında erken çıkış yapılıyor

      case 'mesafe':
      case 'uzunluk':
      default:
        // Mesafe/Uzunluk ölçümü
        this.dimensionService.createDistanceMeasurement(
          nokta1,
          nokta2,
          { birim: 'mm', hassasiyet: this.koordinatBas, olcumTuru }
        );
        break;
    }

    // 🎯 sessionId'yi sessionId'siz TÜM dimension'lara ata (sadece sonuncuya değil!).
    // Eskiden yalnız son dimension işaretleniyordu → çap ölçümünde 2. dimension sessionId'siz
    // kalıp clearSession ile SİLİNEMİYOR, sahnede birikiyordu (çift çap + çizgi maskeleme +
    // temizlenmeme). Hepsine işaret koy → yeni ölçüm/bitir'de düzgün temizlenir.
    if (manager && this.sessionId) {
      const allDims = manager.getAllDimensions();
      for (const d of allDims) {
        if (d && !d.userData.sessionId) d.userData.sessionId = this.sessionId;
      }
    }

    // Dimension'ları güncelle
    this.dimensionService.updateAll();

    if (this.anaGosterim) {
      this.anaGosterim.ciz();
    }
  }

  /**
   * 🎯 NOKTA KOY: Hover noktasını göster/güncelle (THREE.Points - en üstte)
   */
  private hoverNoktaGoster(konum: THREE.Vector3, kesisim?: any): void {
    if (!this.anaGosterim || !this.anaGosterim.sahneler[0]) return;

    const sahneFiko = this.anaGosterim.sahneler[0] as any;

    // Eğer Points yoksa oluştur
    if (!this.hoverNoktaPoints) {
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute([0, 0, 0], 3));
      const material = new THREE.PointsMaterial({
        color: 0x000000, // 🎯 SİYAH hover noktası
        size: this.noktaKoyBoyutu, // Ayarlardan gelen nokta boyutu
        sizeAttenuation: false, // Kameradan bağımsız sabit boyut
        transparent: true,
        opacity: 1.0,
        depthTest: false,
        depthWrite: false
      });
      this.hoverNoktaPoints = new THREE.Points(geometry, material);
      this.hoverNoktaPoints.name = "HoverNokta";
      this.hoverNoktaPoints.renderOrder = 99999; // En üstte
      this.hoverNoktaPoints.frustumCulled = false;
      sahneFiko.add(this.hoverNoktaPoints);
    } else {
      // Points zaten varsa, boyutu güncelle (ayarlar değişmiş olabilir)
      (this.hoverNoktaPoints.material as THREE.PointsMaterial).size = this.noktaKoyBoyutu;
    }

    // 🎯 v3.2.0: Tüm snap türlerinde SİYAH renk
    let color = 0x000000; // 🎯 SİYAH - varsayılan (yüzey)
    if (kesisim) {
      const altObje = (kesisim as any).altObje;
      const obj = kesisim.object;

      if (altObje?.type === 'Points' || obj?.type === 'Points') {
        color = 0x000000; // 🎯 SİYAH - nokta
      } else if (altObje?.type === 'LineSegments' || altObje?.type === 'LineSegments2' ||
                 obj?.type === 'LineSegments' || obj?.type === 'LineSegments2') {
        color = 0x000000; // 🎯 SİYAH - kenar
      }
    }

    (this.hoverNoktaPoints.material as THREE.PointsMaterial).color.setHex(color);
    // Pozisyonu güncelle
    const posAttr = this.hoverNoktaPoints.geometry.attributes['position'] as THREE.BufferAttribute;
    posAttr.setXYZ(0, konum.x, konum.y, konum.z);
    posAttr.needsUpdate = true;
    // Bounding sphere güncelle (frustum culling için gerekli)
    this.hoverNoktaPoints.geometry.computeBoundingSphere();
    this.hoverNoktaPoints.visible = true;

    if (this.anaGosterim.ciz) {
      this.anaGosterim.ciz();
    }
  }

  /**
   * 🎯 NOKTA KOY: Hover noktasını temizle
   */
  private hoverNoktaTemizle(): void {
    if (this.hoverNoktaPoints) {
      this.hoverNoktaPoints.visible = false;
    }
    if (this.hoverCrosshairGroup) {
      this.hoverCrosshairGroup.visible = false;
    }
    if (this.anaGosterim && this.anaGosterim.ciz) {
      this.anaGosterim.ciz();
    }
  }

  /**
   * 🎯 NOKTA KOY: Kalıcı nokta ekle (tıklandığında) - THREE.Points (GPU, en üstte)
   */
  private kaliciNoktaEkle(konum: THREE.Vector3, label: string): void {
    if (!this.anaGosterim || !this.anaGosterim.sahneler[0]) return;

    const sahneFiko = this.anaGosterim.sahneler[0] as any;

    // Kalıcı nokta Points oluştur (GPU accelerated)
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute([konum.x, konum.y, konum.z], 3));
    const cizgiRenk = parseInt(this.cizimRengi.replace('#', ''), 16);
    const material = new THREE.PointsMaterial({
      color: cizgiRenk, // Seçim rengi (altın sarısı)
      size: this.noktaKoyBoyutu * 0.5, // Kalıcı noktalar hover'dan biraz küçük
      sizeAttenuation: false, // Kameradan bağımsız sabit boyut
      transparent: true,
      opacity: 1.0,
      depthTest: false,
      depthWrite: false
    });
    const noktaPoints = new THREE.Points(geometry, material);
    noktaPoints.name = `KaliciNokta_${Date.now()}`;
    noktaPoints.renderOrder = 99998; // En üstte (hover'dan biraz altta)
    noktaPoints.frustumCulled = false;

    // Sahneye ekle ve listeye kaydet
    sahneFiko.add(noktaPoints);
    this.kaliciNoktalar.push(noktaPoints);

    if (this.anaGosterim.ciz) {
      this.anaGosterim.ciz();
    }
  }

  /**
   * 🎯 NOKTA KOY: Tüm kalıcı noktaları temizle
   * Base class metodunu override eder
   */
  protected override kaliciNoktalariTemizle(): void {
    if (!this.kaliciNoktalar || this.kaliciNoktalar.length === 0) return;

    for (const nokta of this.kaliciNoktalar) {
      if (nokta.parent) {
        nokta.parent.remove(nokta);
      }
      nokta.geometry?.dispose();
      (nokta.material as THREE.Material)?.dispose();
    }
    this.kaliciNoktalar = [];

    if (this.anaGosterim && this.anaGosterim.ciz) {
      this.anaGosterim.ciz();
    }
  }

  /**
   * Gösterim grubunu sahne'den çıkar ve temizle
   */
  private gosterimYukleTemizle(): void {
    // Hover noktasını temizle (THREE.Points)
    if (this.hoverNoktaPoints) {
      if (this.hoverNoktaPoints.parent) {
        this.hoverNoktaPoints.parent.remove(this.hoverNoktaPoints);
      }
      this.hoverNoktaPoints.geometry?.dispose();
      (this.hoverNoktaPoints.material as THREE.Material)?.dispose();
      this.hoverNoktaPoints = null;
    }

    // 🎯 Kalıcı noktaları temizle (bitir/destroy'da çağrılır)
    if (this.kaliciNoktalar && this.kaliciNoktalar.length > 0) {
      for (const nokta of this.kaliciNoktalar) {
        if (nokta.parent) {
          nokta.parent.remove(nokta);
        }
        nokta.geometry?.dispose();
        (nokta.material as THREE.Material)?.dispose();
      }
      this.kaliciNoktalar = [];
    }

    if (this.fikoOlcumCizgisi) {
      // Sahneden çıkar
      if (this.fikoOlcumCizgisi.parent) {
        this.fikoOlcumCizgisi.parent.remove(this.fikoOlcumCizgisi);
      }
      // Kaynakları serbest bırak
      this.fikoOlcumCizgisi.dispose();
      this.fikoOlcumCizgisi = null;
    }
  }

  /**
   * 🎯 HOVER AYARLARI: localStorage'dan hover modunu yükle
   * Kumpas/Cetvel geçişlerinde hover modunun kapanmasını önler
   * Base class metodunu override eder
   */
  protected override hoverAyarlariYukle(): void {
    if (!this.fikoSecim1) return;
    try {
      const kaydedilen = localStorage.getItem('FikoThreeJSAyarlari');
      if (kaydedilen) {
        const ayarlar = JSON.parse(kaydedilen);
        // Hover highlight tip ayarları
        if (ayarlar.hoverHighlightMesh !== undefined) {
          this.fikoSecim1.hoverHighlightMesh = ayarlar.hoverHighlightMesh;
        }
        if (ayarlar.hoverHighlightLine !== undefined) {
          this.fikoSecim1.hoverHighlightLine = ayarlar.hoverHighlightLine;
        }
        if (ayarlar.hoverHighlightPoint !== undefined) {
          this.fikoSecim1.hoverHighlightPoint = ayarlar.hoverHighlightPoint;
        }
        if (ayarlar.hoverHighlightEdge !== undefined) {
          this.fikoSecim1.hoverHighlightEdge = ayarlar.hoverHighlightEdge;
        }
        // Genel hover ayarı
        if (ayarlar.hoverHighlightEnabled !== undefined) {
          this.fikoSecim1.hoverHighlightEnabled = ayarlar.hoverHighlightEnabled;
        } else {
          // Ayar yoksa varsayılan true
          this.fikoSecim1.hoverHighlightEnabled = true;
        }
      } else {
        // Ayar dosyası yoksa varsayılan true
        this.fikoSecim1.hoverHighlightEnabled = true;
      }
    } catch (e) {
      // Hata durumunda varsayılan true
      this.fikoSecim1.hoverHighlightEnabled = true;
    }
  }

  /** 🎯 GD&T: tek yüzey form sapması için modele FCF sembolü yerleştir */
  private gdtSembolGoster(tip: 'silindiriklik' | 'duzlemsellik', sapma: number, anchor: THREE.Vector3): void {
    const symbolType = tip === 'silindiriklik' ? GDT_SEMBOL.silindiriklik : GDT_SEMBOL.flatness;
    this.gdtAnnotator.goster([{ symbolType, tolerance: sapma, attachPoint: anchor }]);
  }

  /**
   * 🎯 AKILLI ÇAP: Dairesel kenar üzerinde hover'da çapı otomatik önizle.
   * GÜVENLİ (donmasız): sadece KENAR hover'ında ve kenar DEĞİŞİNCE çalışır; değer BREP
   * metadata'sından (occt_edgeGroups: radius/center) okunur → çember-fit YOK, O(1).
   */
  private akilliCapOnizle(kesisim: any): void {
    const e = kesisim?.altObje || kesisim?.object;
    const kenarHover = e && (e.type === 'LineSegments2' || e.type === 'LineSegments');
    if (!kenarHover) {
      // 🎯 Kenar değil → silindirik YÜZEY hover'ında çap dene, değilse gizle
      this.silindirYuzeyCapOnizle(kesisim, e);
      return;
    }

    const edgeId = this.fikoSecim1?.sonSecilenEdgeGrupIdUzerinde ?? -1;
    if (edgeId < 0) { this.cemberPreviewGizle(); return; }
    if (edgeId === this._sonCemberEdgeId) return; // kenar değişmedi → tekrar hesaplama (donma önleme)
    this._sonCemberEdgeId = edgeId;

    // occt metadata taşıyan gövdeyi bul
    let cizim: any = kesisim?.object;
    while (cizim && cizim.type !== 'FikoCizimYalin' && !cizim.occt_edgeGroups) cizim = cizim.parent;
    const edgeGroups = cizim?.occt_edgeGroups;
    if (!edgeGroups || edgeId >= edgeGroups.length) { this.cemberPreviewGizle(); return; }

    const eg = edgeGroups[edgeId];
    const r = eg?.radius;
    const c = eg?.center;
    if (r && r > 0 && c) {
      const merkez = new THREE.Vector3(c.x ?? c[0] ?? 0, c.y ?? c[1] ?? 0, c.z ?? c[2] ?? 0);
      if (cizim?.matrixWorld) merkez.applyMatrix4(cizim.matrixWorld);
      this.cemberPreviewGoster(merkez, r * 2);
    } else {
      this.cemberPreviewGizle();
    }
  }

  /**
   * 🎯 AKILLI ÇAP (YÜZEY): Silindirik/küresel yüzeye gelince çapı önizle.
   * GÜVENLİ: yüzey DEĞİŞİNCE (faceIndex cache) + grup→occt_faceGroups.radius (fit YOK, O(1)).
   * Etiket imlecin değdiği noktada (kesisim.point) gösterilir.
   */
  private silindirYuzeyCapOnizle(kesisim: any, mesh: any): void {
    const faceIdx = kesisim?.face?.a ?? kesisim?.faceIndex ?? -1;
    const grupAttr = mesh?.geometry?.attributes?.grup;
    if (faceIdx < 0 || !grupAttr || !kesisim?.point) { this.cemberPreviewGizle(); return; }

    const key = 1000000 + faceIdx; // kenar id'leriyle çakışmasın
    if (key === this._sonCemberEdgeId) return; // yüzey değişmedi → tekrar hesaplama yok
    this._sonCemberEdgeId = key;

    const grp = Math.round(grupAttr.getZ(faceIdx));
    let cizim: any = kesisim.object;
    while (cizim && cizim.type !== 'FikoCizimYalin' && !cizim.occt_faceGroups) cizim = cizim.parent;
    const fg = cizim?.occt_faceGroups?.[grp];
    const r = fg?.radius;
    if (r && r > 0) {
      // Çapı, imlecin yüzeye değdiği noktada göster (eksendeki merkez parçanın içinde kalabilir)
      this.cemberPreviewGoster(kesisim.point.clone(), r * 2);
    } else {
      this.cemberPreviewGizle();
    }
  }

  /** 🎯 AKILLI ÇAP: Çap önizleme sprite'ı göster (canvas reuse, değer değişince yeniden çiz). */
  private cemberPreviewGoster(merkez: THREE.Vector3, cap: number): void {
    if (!this.anaGosterim?.sahneler[0]) return;
    const sahneFiko = this.anaGosterim.sahneler[0] as any;
    const metin = `⌀ ${cap.toFixed(this.koordinatBas)} mm`;

    if (!this.cemberPreviewSprite) {
      const canvas = document.createElement('canvas');
      canvas.width = 256; canvas.height = 64;
      this.cemberPreviewCanvas = canvas;
      this.cemberPreviewTexture = new THREE.CanvasTexture(canvas);
      this.cemberPreviewTexture.minFilter = THREE.LinearFilter;
      this.cemberPreviewTexture.magFilter = THREE.LinearFilter;
      const mat = new THREE.SpriteMaterial({ map: this.cemberPreviewTexture, depthTest: false, transparent: true });
      this.cemberPreviewSprite = new THREE.Sprite(mat);
      (this.cemberPreviewSprite as any).raycast = () => {}; // 🎯 tıklamayı engelleme (seçim sprite'a çarpmasın)
      this.cemberPreviewSprite.name = 'KumpasCemberPreview';
      this.cemberPreviewSprite.renderOrder = 99999;
      this.cemberPreviewSprite.frustumCulled = false;
      sahneFiko.add(this.cemberPreviewSprite);
    }

    if (this.cemberPreviewSonCap !== cap) {
      this.cemberPreviewSonCap = cap;
      const canvas = this.cemberPreviewCanvas!;
      const ctx = canvas.getContext('2d')!;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.font = 'bold 28px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const tw = ctx.measureText(metin).width;
      const pad = 12;
      ctx.fillStyle = 'rgba(0,0,0,0.78)';
      (ctx as any).roundRect((canvas.width - tw) / 2 - pad, 8, tw + pad * 2, 48, 6);
      ctx.fill();
      ctx.fillStyle = '#4aa8ff';
      ctx.fillText(metin, canvas.width / 2, 32);
      this.cemberPreviewTexture!.needsUpdate = true;
    }

    const cv = this.cemberPreviewCanvas!;
    this.cemberPreviewSprite.position.copy(merkez);
    this.cemberPreviewSprite.scale.set(cv.width * 0.012, cv.height * 0.012, 1);
    this.cemberPreviewSprite.visible = true;
    if (this.anaGosterim.ciz) this.anaGosterim.ciz();
  }

  /** 🎯 AKILLI ÇAP: Önizlemeyi gizle */
  private cemberPreviewGizle(): void {
    this._sonCemberEdgeId = undefined; // re-hover'da tekrar hesaplansın
    if (this.cemberPreviewSprite?.visible) {
      this.cemberPreviewSprite.visible = false;
      if (this.anaGosterim?.ciz) this.anaGosterim.ciz();
    }
  }

  /** 🎯 AKILLI ÇAP: Kaynakları tamamen temizle */
  private cemberPreviewTemizle(): void {
    if (this.cemberPreviewSprite) {
      if (this.cemberPreviewSprite.parent) this.cemberPreviewSprite.parent.remove(this.cemberPreviewSprite);
      this.cemberPreviewTexture?.dispose();
      (this.cemberPreviewSprite.material as THREE.SpriteMaterial)?.dispose();
      this.cemberPreviewSprite = null;
      this.cemberPreviewCanvas = null;
      this.cemberPreviewTexture = null;
      this.cemberPreviewSonCap = -1;
    }
    this._sonCemberEdgeId = undefined;
  }

  /** 🎯 ALAN 3D: Yüzey alanını sahnede merkeze etiketle (canvas reuse). */
  private alanEtiketGoster(merkez: THREE.Vector3, alan: number): void {
    if (!this.anaGosterim?.sahneler[0]) return;
    const sahneFiko = this.anaGosterim.sahneler[0] as any;
    const metin = `Alan: ${alan.toFixed(this.koordinatBas)} mm²`;

    if (!this.alanEtiketSprite) {
      const canvas = document.createElement('canvas');
      canvas.width = 320; canvas.height = 64;
      this.alanEtiketCanvas = canvas;
      this.alanEtiketTexture = new THREE.CanvasTexture(canvas);
      this.alanEtiketTexture.minFilter = THREE.LinearFilter;
      this.alanEtiketTexture.magFilter = THREE.LinearFilter;
      const mat = new THREE.SpriteMaterial({ map: this.alanEtiketTexture, depthTest: false, transparent: true });
      this.alanEtiketSprite = new THREE.Sprite(mat);
      (this.alanEtiketSprite as any).raycast = () => {}; // 🎯 tıklamayı engelleme
      this.alanEtiketSprite.name = 'KumpasAlanEtiket';
      this.alanEtiketSprite.renderOrder = 99998;
      this.alanEtiketSprite.frustumCulled = false;
      sahneFiko.add(this.alanEtiketSprite);
    }

    if (this.alanEtiketSonAlan !== alan) {
      this.alanEtiketSonAlan = alan;
      const canvas = this.alanEtiketCanvas!;
      const ctx = canvas.getContext('2d')!;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.font = 'bold 26px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const tw = ctx.measureText(metin).width;
      const pad = 12;
      ctx.fillStyle = 'rgba(0,0,0,0.78)';
      (ctx as any).roundRect((canvas.width - tw) / 2 - pad, 8, tw + pad * 2, 48, 6);
      ctx.fill();
      ctx.fillStyle = '#7CFC8A';
      ctx.fillText(metin, canvas.width / 2, 32);
      this.alanEtiketTexture!.needsUpdate = true;
    }

    const cv = this.alanEtiketCanvas!;
    this.alanEtiketSprite.position.copy(merkez);
    this.alanEtiketSprite.scale.set(cv.width * 0.012, cv.height * 0.012, 1);
    this.alanEtiketSprite.visible = true;
    if (this.anaGosterim.ciz) this.anaGosterim.ciz();
  }

  /** 🎯 ALAN 3D: Etiketi gizle */
  private alanEtiketGizle(): void {
    if (this.alanEtiketSprite?.visible) {
      this.alanEtiketSprite.visible = false;
      if (this.anaGosterim?.ciz) this.anaGosterim.ciz();
    }
  }

  /** 🎯 ALAN 3D: Kaynakları temizle */
  private alanEtiketTemizle(): void {
    if (this.alanEtiketSprite) {
      if (this.alanEtiketSprite.parent) this.alanEtiketSprite.parent.remove(this.alanEtiketSprite);
      this.alanEtiketTexture?.dispose();
      (this.alanEtiketSprite.material as THREE.SpriteMaterial)?.dispose();
      this.alanEtiketSprite = null;
      this.alanEtiketCanvas = null;
      this.alanEtiketTexture = null;
      this.alanEtiketSonAlan = -1;
    }
  }

  bitir() {
    // 🎯 GD&T FCF sembollerini temizle
    this.gdtAnnotator?.temizle();
    // 🎯 AKILLI ÇAP: önizleme kaynaklarını temizle
    this.cemberPreviewTemizle();
    // 🎯 ALAN 3D: etiket kaynaklarını temizle
    this.alanEtiketTemizle();

    // Tüm subscription'ları temizle
    this.subsList.forEach(element => {
      element.unsubscribe();
    });
    this.subsList = []; // Array'i temizle

    this.haritaOlaylar.forEach((value: any, key: string) => {
      if (value) value();
    });
    this.haritaOlaylar.clear();

    // 🎯 YENİ DIMENSION SİSTEMİ: Sadece bu session'daki ölçümleri temizle
    // NOT: dispose() yerine clearSession() kullanıyoruz, kalıcı ölçümler korunsun
    if (this.dimensionService && this.sessionId) {
      const manager = this.dimensionService.getManager();
      if (manager?.clearSession) {
        manager.clearSession(this.sessionId);
      }
      // NOT: dispose() çağırmıyoruz - service paylaşımlı olabilir
      this.dimensionService = null;
    }

    // Gösterim temizle
    this.gosterimYukleTemizle();

    if (this.fikoSecim1) {
      this.fikoSecim1.temizle(); // Highlight'ları temizle
      this.fikoSecim1.aktif = false; // Shared instance
      // 🎯 Kalıcı seçim modunu kapat (hover modu ayarlardan gelir, dokunma!)
      this.fikoSecim1.kaliciSecimModu = false;
      // 🎯 ARAÇ STATE RESTORE: baslat'ta yedeklenen filtre/renk/mod/raycast ayarlarını
      // geri yükle — araçlar arası (kumpas↔cetvel) highlight/seçim karışmasını önler
      if (this._secimDurumYedekli) {
        this.fikoSecim1.durumGeriYukle?.();
        this._secimDurumYedekli = false;
      }
      this.fikoSecim1 = null;
      if (this.anaGosterim && this.anaGosterim.ciz) {
        this.anaGosterim.ciz();
      }
    }
    if (this.fikoRaycastEksenSec) {
      this.fikoRaycastEksenSec.bitir();
      this.fikoRaycastEksenSec.dispose();
      this.fikoRaycastEksenSec = null;
    }

    //@ts-ignore
    document.body.style.cursor = "inherit";

    // 🎯 SESSION: Session ID'yi temizle
    this.sessionId = '';
    this.kaliciOlcumlerGrubu = null;
  }

  ngOnDestroy(): void {
    // Tüm subscription'ları temizle
    this.subsList.forEach(element => {
      element.unsubscribe();
    });
    this.subsList = []; // Array'i temizle

    this.haritaOlaylar.forEach((value: any, key: string) => {
      if (value) value();
    });
    this.haritaOlaylar.clear();

    // 🎯 YENİ DIMENSION SİSTEMİ: Sadece bu session'daki ölçümleri temizle
    // NOT: dispose() yerine clearSession() kullanıyoruz, kalıcı ölçümler korunsun
    if (this.dimensionService && this.sessionId) {
      const manager = this.dimensionService.getManager();
      if (manager?.clearSession) {
        manager.clearSession(this.sessionId);
      }
      // NOT: dispose() çağırmıyoruz - service paylaşımlı olabilir
      this.dimensionService = null;
    }

    // Gösterim temizle
    this.gosterimYukleTemizle();

    // ⚡ GEOMETRIC SNAP: Temizle
    this.geometricSnap.dispose();

    // 🎯 NOKTA KOY: Crosshair temizle
    if (this.hoverCrosshairGroup) {
      if (this.hoverCrosshairGroup.parent) {
        this.hoverCrosshairGroup.parent.remove(this.hoverCrosshairGroup);
      }
      this.hoverCrosshairGroup.traverse((child) => {
        if ((child as THREE.Mesh).geometry) {
          (child as THREE.Mesh).geometry.dispose();
        }
        if ((child as THREE.Mesh).material) {
          ((child as THREE.Mesh).material as THREE.Material).dispose();
        }
      });
      this.hoverCrosshairGroup = null;
    }

    // 🎯 NOKTA KOY: Kalıcı noktaları temizle
    this.kaliciNoktalariTemizle();

    if (this.fikoSecim1) {
      this.fikoSecim1.temizle(); // Highlight'ları temizle
      this.fikoSecim1.aktif = false; // Shared instance
      // 🎯 Kalıcı seçim modunu kapat (hover modu ayarlardan gelir, dokunma!)
      this.fikoSecim1.kaliciSecimModu = false;
      // 🎯 ARAÇ STATE RESTORE: baslat'ta yedeklenen filtre/renk/mod/raycast ayarlarını
      // geri yükle — araçlar arası (kumpas↔cetvel) highlight/seçim karışmasını önler
      if (this._secimDurumYedekli) {
        this.fikoSecim1.durumGeriYukle?.();
        this._secimDurumYedekli = false;
      }
      this.fikoSecim1 = null;
      if (this.anaGosterim && this.anaGosterim.ciz) {
        this.anaGosterim.ciz();
      }
    }
    if (this.fikoRaycastEksenSec) {
      this.fikoRaycastEksenSec.bitir();
      this.fikoRaycastEksenSec.dispose();
      this.fikoRaycastEksenSec = null;
    }

    //@ts-ignore
    document.body.style.cursor = "inherit";
  }

  // ========== KALICI ÖLÇÜM SİSTEMİ ==========

  /**
   * 🎯 SESSION ID: Benzersiz session ID üret
   * Format: kumpas_YYYYMMDD_HHmmss_random
   */
  private generateSessionId(): string {
    const now = new Date();
    const dateStr = now.toISOString().replace(/[-:T.Z]/g, '').slice(0, 14);
    const random = Math.random().toString(36).substring(2, 8);
    return `kumpas_${dateStr}_${random}`;
  }

  /**
   * 🎯 KALICI ÖLÇÜMLER GRUBU: FikoKaynakca altındaki grubu bul
   */
  private kaliciOlcumlerGrubuBul(sahne: any): void {
    // FikoKaynakca'yı bul - önce doğrudan sahne'de ara
    let kaynakca = sahne.getObjectByProperty?.('type', 'FikoKaynakca') as FikoKaynakca;

    // Bulunamazsa sahne.children içinde recursive ara
    if (!kaynakca) {
      sahne.traverse?.((child: any) => {
        if (child.type === 'FikoKaynakca') {
          kaynakca = child;
        }
      });
    }

    if (kaynakca && kaynakca.kaliciOlcumler) {
      this.kaliciOlcumlerGrubu = kaynakca.kaliciOlcumler;
      // console.log('[Kumpas] Kalıcı ölçümler grubu bulundu:', this.kaliciOlcumlerGrubu);
    } else {
      console.warn('[Kumpas] FikoKaynakca veya kaliciOlcumler bulunamadı!', { kaynakca, sahne });
    }
  }

  /**
   * 🎯 ÖLÇÜM KALICI OLSUN: Mevcut ölçümü kalıcı gruba aktar
   * Bu metod template'ten çağrılır
   */
  olcumKaliciYap(): void {
    if (!this.kaliciOlcumlerGrubu) {
      console.warn('[Kumpas] Kalıcı ölçümler grubu bulunamadı');
      return;
    }

    // Mevcut ölçüm var mı kontrol et (kumpas tek seçimli)
    if (!this.sonuc) {
      console.warn('[Kumpas] Aktif ölçüm yok');
      return;
    }

    // 🎯 v3.3.2 FIX: Koordinat ölçümü için doğrudan eski sisteme git
    // Koordinat ölçümü her zaman FikoOlcumCizgisi kullanır (useNewDimensionSystem geçici false yapılıyor)
    const koordinatOlcumu = this.sonuc.nokta && !this.sonuc.cizgi && !this.sonuc.yuzey;

    // Ölçüm başlığını oluştur (kumpas'a özel)
    // 🎯 v3.2.8: Öncelik sırası düzeltildi - Çap > Yarıçap > Alan > Uzunluk
    let baslik = '';

    // 1. Çap/yarıçap kontrolü (cember verisinden)
    if (this.sonuc.cizgi?.cember?.cap) {
      baslik = `Ø ${Number(this.sonuc.cizgi.cember.cap).toFixed(this.koordinatBas)} mm`;
    } else if (this.sonuc.cizgi?.cember?.yariCap) {
      baslik = `R ${Number(this.sonuc.cizgi.cember.yariCap).toFixed(this.koordinatBas)} mm`;
    }
    // 2. Alan (sadece çap/yarıçap yoksa)
    else if (this.sonuc.yuzey?.alan) {
      baslik = `Alan: ${Number(this.sonuc.yuzey.alan).toFixed(this.alanBas)} mm²`;
    }
    // 3. Uzunluk
    else if (this.sonuc.cizgi?.uzunluk) {
      baslik = `${this.sonuc.cizgi.uzunluk.toFixed(this.koordinatBas)} mm`;
    }
    // 4. Nokta/Koordinat
    else if (this.sonuc.nokta) {
      baslik = `X:${Number(this.sonuc.nokta.x).toFixed(this.koordinatBas)} Y:${Number(this.sonuc.nokta.y).toFixed(this.koordinatBas)} Z:${Number(this.sonuc.nokta.z).toFixed(this.koordinatBas)}`;
    }
    // 5. Varsayılan
    else {
      baslik = 'Ölçüm';
    }

    // 🎯 v3.3.2 FIX: Koordinat ölçümü için doğrudan eski sisteme atla
    // Koordinat her zaman FikoOlcumCizgisi kullanır, DimensionManager'da dimension yok!
    if (koordinatOlcumu) {
      // Eski sistem bloğuna atla (aşağıda işlenecek)
    } else if (this.useNewDimensionSystem && this.dimensionService) {
      const manager = this.dimensionService.getManager();
      if (manager) {
        const allDimensions = manager.getAllDimensions();
        if (allDimensions.length > 0) {
          // Son eklenen dimension'ı al
          const sonDimension = allDimensions[allDimensions.length - 1];

          // 🎯 Dimension'ı THREE.Group olarak klonla (Dimension sınıfını değil, sadece görsel elemanları)
          const klonGrup = new THREE.Group();
          klonGrup.name = `KaliciOlcum_${baslik}`;

          // Tüm child'ları derin klonla (mesh, line, sprite vb.)
          sonDimension.children.forEach((child: THREE.Object3D) => {
            const klonChild = this.derinKlonla(child);
            klonGrup.add(klonChild);
          });

          // Pozisyon ve rotasyonu kopyala
          klonGrup.position.copy(sonDimension.position);
          klonGrup.rotation.copy(sonDimension.rotation);
          klonGrup.scale.copy(sonDimension.scale);

          // 🎯 v3.0.5: Orijinal ölçüm noktalarını userData'ya kaydet (sürükleme için kritik!)
          let noktalarKaydedildi = false;
          if (typeof (sonDimension as any).getOriginalPoints === 'function') {
            const orijinalNoktalar = (sonDimension as any).getOriginalPoints();
            if (orijinalNoktalar?.pointA && orijinalNoktalar?.pointB) {
              klonGrup.userData.startPoint = orijinalNoktalar.pointA.clone();
              klonGrup.userData.endPoint = orijinalNoktalar.pointB.clone();
              noktalarKaydedildi = true;
            }
          }

          // 🎯 v3.2.10: Çap/yarıçap ölçümlerinde nokta1/nokta2'yi kullan (TAŞIMA İÇİN KRİTİK!)
          if (!noktalarKaydedildi && this.sonuc.cizgi?.cember?.nokta1 && this.sonuc.cizgi?.cember?.nokta2) {
            klonGrup.userData.startPoint = this.sonuc.cizgi.cember.nokta1.clone();
            klonGrup.userData.endPoint = this.sonuc.cizgi.cember.nokta2.clone();
            noktalarKaydedildi = true;
          }

          // 🎯 v3.2.10: Hala kaydedilmediyse sonuç noktalarından al (fallback)
          if (!noktalarKaydedildi && this.sonuc?.nokta) {
            const nokta = this.sonuc.nokta;
            klonGrup.userData.startPoint = new THREE.Vector3(Number(nokta.x), Number(nokta.y), Number(nokta.z));
            klonGrup.userData.endPoint = new THREE.Vector3(Number(nokta.x), Number(nokta.y), Number(nokta.z));
            noktalarKaydedildi = true;
          }

          // 🎯 v3.2.5: Sabit boyut sistemi için varsayılan ayar
          klonGrup.userData.sabitBoyut = true;

          // 🎯 v3.2.9: Gerçek değerleri userData'ya kaydet (düzenleme dialogu için)
          // 🎯 gercekDeger - dialog tekrar açıldığında bu değer kullanılacak
          if (this.sonuc.cizgi?.cember?.cap) {
            klonGrup.userData.gercekDeger = this.sonuc.cizgi.cember.cap;  // 🎯 EN ÖNEMLİ
            klonGrup.userData.cap = this.sonuc.cizgi.cember.cap;
            klonGrup.userData.diameter = this.sonuc.cizgi.cember.cap;
          } else if (this.sonuc.cizgi?.cember?.yariCap) {
            klonGrup.userData.gercekDeger = this.sonuc.cizgi.cember.yariCap;
            klonGrup.userData.yaricap = this.sonuc.cizgi.cember.yariCap;
            klonGrup.userData.radius = this.sonuc.cizgi.cember.yariCap;
          } else if (this.sonuc.cizgi?.uzunluk) {
            klonGrup.userData.gercekDeger = this.sonuc.cizgi.uzunluk;
            klonGrup.userData.uzunluk = this.sonuc.cizgi.uzunluk;
          } else if (this.sonuc.yuzey?.alan) {
            klonGrup.userData.gercekDeger = this.sonuc.yuzey.alan;
            klonGrup.userData.alan = this.sonuc.yuzey.alan;
          }

          // Ölçüm tipi
          if (this.sonuc.cizgi?.cember) {
            klonGrup.userData.olcumTipi = 'cap';
          } else if (this.sonuc.cizgi?.uzunluk) {
            klonGrup.userData.olcumTipi = 'uzunluk';
          } else if (this.sonuc.yuzey?.alan) {
            klonGrup.userData.olcumTipi = 'alan';
          }

          // Kalıcı gruba ekle
          this.kaliciOlcumlerGrubu.kaliciOlcumEkle(klonGrup, baslik, this.sessionId);

          // 🎯 Sahne ağacını güncelle (ürün ağacı için gerekli)
          if (this.kaliciOlcumlerGrubu.sahne) {
            (this.kaliciOlcumlerGrubu.sahne as any).dispatchEvent?.({ type: 'childadded' });
          }
          this.FikoYT?.degisti?.emit();   // 🎯 Angular ürün ağacını yenile → kalıcı ölçüm ağaçta görünsün
      try { this.fikoSecim1?.temizle?.(); } catch (e) { }   // 📌 sonrası seçim highlight noktalarını temizle (orphan kalmasın)

          // UI güncelle
          if (this.anaGosterim?.ciz) {
            this.anaGosterim.ciz();
          }
          this.CD.detectChanges();
          return;
        }
      }
    }

    // 🎯 ESKİ SİSTEM: FikoOlcumCizgisi kullanılıyorsa
    if (this.fikoOlcumCizgisi) {
      const klonGrup = this.fikoOlcumCizgisi.clone(true) as THREE.Group;
      klonGrup.name = `KaliciOlcum_${baslik}`;

      // 🎯 v3.3.4 FIX: FikoOlcumCizgisi klonlandığında class property'leri kopyalanmıyor
      // Klonun olcumTuru property'sini orijinalden kopyala
      if ((klonGrup as any).olcumTuru !== undefined) {
        (klonGrup as any).olcumTuru = this.fikoOlcumCizgisi.olcumTuru;
        // console.log('[Kumpas] v3.3.4 Klon olcumTuru güncellendi:', this.fikoOlcumCizgisi.olcumTuru);
      }

      // 🎯 v3.3.2 FIX: Koordinat ölçümünde userData'ya koordinat noktasını kaydet
      if (koordinatOlcumu && this.sonuc.nokta) {
        klonGrup.userData.koordinatNoktasi = {
          x: this.sonuc.nokta.x,
          y: this.sonuc.nokta.y,
          z: this.sonuc.nokta.z
        };
        klonGrup.userData.olcumTuru = 'koordinat';
        klonGrup.userData.olcumTipi = 'koordinat';

        // 🎯 v3.3.3 FIX: Koordinat ölçümünde klonlanmış okları temizle
        // Clone sırasında eski ölçüm tiplerinden kalan oklar da kopyalanıyor
        const oklariTemizle: THREE.Object3D[] = [];
        klonGrup.traverse((child) => {
          if (child.name === 'OlcumOk1' || child.name === 'OlcumOk2' ||
              child.name === 'DiameterArrow1' || child.name === 'DiameterArrow2' ||
              child.name === 'RadiusArrow' || child.name === 'ArcArrow1' || child.name === 'ArcArrow2' ||
              child.name?.includes('Arrow') || child.name?.includes('Ok')) {
            oklariTemizle.push(child);
          }
        });
        oklariTemizle.forEach(ok => {
          if (ok.parent) ok.parent.remove(ok);
          if ((ok as THREE.Mesh).geometry) (ok as THREE.Mesh).geometry.dispose();
          if ((ok as THREE.Mesh).material) {
            const mat = (ok as THREE.Mesh).material;
            if (Array.isArray(mat)) mat.forEach(m => m.dispose());
            else (mat as THREE.Material).dispose();
          }
        });
      }

      // 🎯 v3.3.5 FIX: Klon FikoOlcumCizgisi ise sabitBoyut ve sahne property'lerini kopyala
      if ((klonGrup as any).sabitBoyut !== undefined) {
        (klonGrup as any).sabitBoyut = this.fikoOlcumCizgisi.sabitBoyut;
        (klonGrup as any).sahne = this.fikoOlcumCizgisi.sahne;
        (klonGrup as any).draggable = this.fikoOlcumCizgisi.draggable; // 🎯 v3.3.6: Sürükleme için gerekli

        // 🎯 v3.3.6 FIX: nokta1 ve nokta2 property'lerini de kopyala (serbest taşıma için kritik)
        if ((klonGrup as any).birinciNokta && this.fikoOlcumCizgisi.birinciNokta) {
          (klonGrup as any).birinciNokta.copy(this.fikoOlcumCizgisi.birinciNokta);
        }
        if ((klonGrup as any).ikinciNokta && this.fikoOlcumCizgisi.ikinciNokta) {
          (klonGrup as any).ikinciNokta.copy(this.fikoOlcumCizgisi.ikinciNokta);
        }

        // 🎯 v3.3.6 FIX: Yazının scale'ini orijinalden kopyala (kameraYonDegisti yanlış hesaplıyor)
        // Orijinal yazının scale'ini klona kopyala
        const orijinalYazi = this.fikoOlcumCizgisi.yaziSprite;
        if (orijinalYazi) {
          // Klondaki yazıyı bul
          klonGrup.traverse((child: THREE.Object3D) => {
            if (child instanceof THREE.Sprite && child !== orijinalYazi) {
              // Orijinal scale'i kopyala
              child.scale.copy(orijinalYazi.scale);
              // originalScale userData'sını da güncelle
              if (orijinalYazi.userData.originalScale) {
                child.userData.originalScale = orijinalYazi.userData.originalScale.clone();
              }
            }
          });
        }
        // NOT: kameraYonDegisti çağırmıyoruz - mevcut scale korunuyor
      }

      // Kalıcı gruba ekle
      this.kaliciOlcumlerGrubu.kaliciOlcumEkle(klonGrup, baslik, this.sessionId);

      // 🎯 v3.3.1 FIX: Orijinal aktif ölçümü sil (klon kalıcı gruba eklendi, orijinale gerek yok)
      // Bu sayede sahnede iki ölçüm görünmez
      if (this.fikoOlcumCizgisi.parent) {
        this.fikoOlcumCizgisi.parent.remove(this.fikoOlcumCizgisi);
      }
      this.fikoOlcumCizgisi.dispose();
      this.fikoOlcumCizgisi = null;

      // 🎯 v3.3.2 FIX: Koordinat ölçümünde geçici kalıcı noktaları da temizle
      // kaliciNoktaEkle() ile eklenen Points objeleri - bunlar geçici, kalıcı gruba değil sahneye ekleniyor
      if (koordinatOlcumu) {
        this.kaliciNoktalariTemizle();
      }

      // UI güncelle
      if (this.anaGosterim?.ciz) {
        this.anaGosterim.ciz();
      }
      this.FikoYT?.degisti?.emit();   // 🎯 Angular ürün ağacını yenile → kalıcı ölçüm ağaçta görünsün
      try { this.fikoSecim1?.temizle?.(); } catch (e) { }   // 📌 sonrası seçim highlight noktalarını temizle (orphan kalmasın)
      this.CD.detectChanges();
    }
  }

  /**
   * 🎯 KALICI ÖLÇÜM VAR MI: Mevcut aktif ölçüm var mı kontrol et
   */
  get kaliciYapilabilirMi(): boolean {
    return this.sonuc !== null && this.kaliciOlcumlerGrubu !== null;
  }

  /**
   * 🎯 DERİN KLONLAMA: Geometri ve materyalleri ayrı ayrı klonlar
   * DimensionManager.clearAll() çağrıldığında kalıcı ölçümlerin silinmesini önler
   */
  private derinKlonla(obj: THREE.Object3D): THREE.Object3D {
    // Mesh için derin klonlama
    if (obj instanceof THREE.Mesh) {
      const yeniGeometri = obj.geometry?.clone();
      let yeniMateryal: THREE.Material | THREE.Material[];

      if (Array.isArray(obj.material)) {
        yeniMateryal = obj.material.map(m => m.clone());
      } else if (obj.material) {
        yeniMateryal = obj.material.clone();
      } else {
        yeniMateryal = new THREE.MeshBasicMaterial();
      }

      const yeniMesh = new THREE.Mesh(yeniGeometri, yeniMateryal);
      yeniMesh.name = obj.name;
      yeniMesh.position.copy(obj.position);
      yeniMesh.rotation.copy(obj.rotation);
      yeniMesh.scale.copy(obj.scale);
      yeniMesh.visible = obj.visible;
      yeniMesh.renderOrder = obj.renderOrder;
      yeniMesh.userData = { ...obj.userData };

      // Çocukları recursive olarak klonla
      obj.children.forEach(child => {
        yeniMesh.add(this.derinKlonla(child));
      });

      return yeniMesh;
    }

    // Line/LineSegments için derin klonlama
    if (obj instanceof THREE.Line || obj instanceof THREE.LineSegments) {
      const yeniGeometri = obj.geometry?.clone();
      let yeniMateryal: THREE.Material | THREE.Material[];

      if (Array.isArray(obj.material)) {
        yeniMateryal = obj.material.map(m => m.clone());
      } else if (obj.material) {
        yeniMateryal = obj.material.clone();
      } else {
        yeniMateryal = new THREE.LineBasicMaterial();
      }

      const YeniSinif = obj.constructor as new (geometry?: THREE.BufferGeometry, material?: THREE.Material | THREE.Material[]) => THREE.Line;
      const yeniLine = new YeniSinif(yeniGeometri, yeniMateryal);
      yeniLine.name = obj.name;
      yeniLine.position.copy(obj.position);
      yeniLine.rotation.copy(obj.rotation);
      yeniLine.scale.copy(obj.scale);
      yeniLine.visible = obj.visible;
      yeniLine.renderOrder = obj.renderOrder;
      yeniLine.userData = { ...obj.userData };

      // Çocukları recursive olarak klonla
      obj.children.forEach(child => {
        yeniLine.add(this.derinKlonla(child));
      });

      return yeniLine;
    }

    // Sprite için derin klonlama
    if (obj instanceof THREE.Sprite) {
      const eskiMateryal = obj.material as THREE.SpriteMaterial;
      const yeniMateryal = new THREE.SpriteMaterial();

      // Materyal özelliklerini kopyala
      yeniMateryal.color.copy(eskiMateryal.color);
      yeniMateryal.opacity = eskiMateryal.opacity;
      yeniMateryal.transparent = eskiMateryal.transparent;
      yeniMateryal.depthTest = eskiMateryal.depthTest;
      yeniMateryal.depthWrite = eskiMateryal.depthWrite;
      yeniMateryal.sizeAttenuation = eskiMateryal.sizeAttenuation;

      // 🎯 Texture'ı GERÇEK klonla (canvas içeriğini kopyala)
      if (eskiMateryal.map) {
        const eskiTexture = eskiMateryal.map;
        const eskiImage = eskiTexture.image;

        if (eskiImage instanceof HTMLCanvasElement) {
          // Canvas'ı yeni bir canvas'a kopyala
          const yeniCanvas = document.createElement('canvas');
          yeniCanvas.width = eskiImage.width;
          yeniCanvas.height = eskiImage.height;
          const ctx = yeniCanvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(eskiImage, 0, 0);
          }

          // Yeni CanvasTexture oluştur
          const yeniTexture = new THREE.CanvasTexture(yeniCanvas);
          yeniTexture.minFilter = eskiTexture.minFilter;
          yeniTexture.magFilter = eskiTexture.magFilter;
          yeniTexture.wrapS = eskiTexture.wrapS;
          yeniTexture.wrapT = eskiTexture.wrapT;
          yeniTexture.needsUpdate = true;

          yeniMateryal.map = yeniTexture;
        } else if (eskiImage instanceof HTMLImageElement) {
          // Image için yeni texture oluştur
          const yeniTexture = new THREE.Texture(eskiImage.cloneNode(true) as HTMLImageElement);
          yeniTexture.minFilter = eskiTexture.minFilter;
          yeniTexture.magFilter = eskiTexture.magFilter;
          yeniTexture.needsUpdate = true;
          yeniMateryal.map = yeniTexture;
        } else {
          // Diğer durumlar için basit clone
          yeniMateryal.map = eskiTexture.clone();
          yeniMateryal.map.needsUpdate = true;
        }
      }

      yeniMateryal.needsUpdate = true;

      const yeniSprite = new THREE.Sprite(yeniMateryal);
      yeniSprite.name = obj.name;
      yeniSprite.position.copy(obj.position);
      yeniSprite.rotation.copy(obj.rotation);
      yeniSprite.scale.copy(obj.scale);
      yeniSprite.visible = obj.visible;
      yeniSprite.renderOrder = obj.renderOrder;
      yeniSprite.userData = { ...obj.userData };

      // 🎯 v3.2.5: Sabit boyut sistemi için originalScale ekle
      // Eğer kaynak sprite'ta originalScale varsa kopyala, yoksa mevcut scale'i kaydet
      if (obj.userData.originalScale) {
        yeniSprite.userData.originalScale = (obj.userData.originalScale as THREE.Vector3).clone();
      } else {
        // originalScale yoksa mevcut scale'i base olarak kaydet
        yeniSprite.userData.originalScale = yeniSprite.scale.clone();
      }
      yeniSprite.userData.isLabel = true;

      return yeniSprite;
    }

    // Points için derin klonlama
    if (obj instanceof THREE.Points) {
      const yeniGeometri = obj.geometry?.clone();
      let yeniMateryal: THREE.Material | THREE.Material[];

      if (Array.isArray(obj.material)) {
        yeniMateryal = obj.material.map(m => m.clone());
      } else if (obj.material) {
        yeniMateryal = obj.material.clone();
      } else {
        yeniMateryal = new THREE.PointsMaterial();
      }

      const yeniPoints = new THREE.Points(yeniGeometri, yeniMateryal);
      yeniPoints.name = obj.name;
      yeniPoints.position.copy(obj.position);
      yeniPoints.rotation.copy(obj.rotation);
      yeniPoints.scale.copy(obj.scale);
      yeniPoints.visible = obj.visible;
      yeniPoints.renderOrder = obj.renderOrder;
      yeniPoints.userData = { ...obj.userData };

      return yeniPoints;
    }

    // Group veya diğer Object3D için
    if (obj instanceof THREE.Group || obj instanceof THREE.Object3D) {
      const yeniGrup = new THREE.Group();
      yeniGrup.name = obj.name;
      yeniGrup.position.copy(obj.position);
      yeniGrup.rotation.copy(obj.rotation);
      yeniGrup.scale.copy(obj.scale);
      yeniGrup.visible = obj.visible;
      yeniGrup.renderOrder = obj.renderOrder;
      yeniGrup.userData = { ...obj.userData };

      // Çocukları recursive olarak klonla
      obj.children.forEach(child => {
        yeniGrup.add(this.derinKlonla(child));
      });

      return yeniGrup;
    }

    // Fallback: standart clone (nadir durumlar için)
    console.warn('[Kumpas] Bilinmeyen obje tipi, standart clone kullanılıyor:', (obj as any).type);
    return (obj as THREE.Object3D).clone(true);
  }
}
